// =============================================================================
// Email Express — shared block renderer + headless capture/send routine
// -----------------------------------------------------------------------------
// This module is the single source of truth for how an Email Express email's
// blocks are rendered to the styled HTML preview and captured to per-block PNGs
// for sending to Klaviyo. Both the editor (on-screen preview) and the list page
// (off-screen headless send) consume it, so a list-page send produces pixel-for-
// pixel the same result as an editor send.
// =============================================================================
import { createElement, type CSSProperties, type ReactNode } from "react";
import { createRoot } from "react-dom/client";
import type {
  EmailBlockCopy,
  EmailInstanceCopy,
  EmailLinkConfig,
  EmailUniversalAssets,
  EmailSellingPointsImage,
  EmailDidYouKnowImage,
  UniversalFooterContent,
  UniversalSocialLink,
  EmailFooterOverrides,
} from "@shared/schema";
import {
  emailBlockType,
  resolveEmailBlockHref,
  emailLinkConfigHasUsableLink,
  type EmailCapturedBlock,
  resolveEmailInstanceCopy,
  resolveEmailBodyImagePreference,
  resolveEffectiveEmailFooter,
  resolveEmailLayoutVariant,
  emailVariantNeedsImage,
  emailVariantShowsEyebrow,
  emailVariantShowsPullQuote,
  emailComparisonValueIsCheckmark,
  emailSectionSurfaceKey,
  separateEmailComparisonBlocks,
  resolveProductListVariant,
  emailLayoutVariantsFor,
  type ProductListType,
  safeFooterHref,
  type ProductListVariant,
  computeEmailPolishSourceHash,
  isEmailImageTreatmentEligible,
  resolveEmailImageTreatment,
  resolveEmailProductListImageTreatment,
  collectEmailImageTreatmentUrls,
  productListTileSlot,
  EMAIL_IMAGE_TREATMENT_MAIN_SLOT,
  type EmailImageTreatment,
} from "@shared/schema";
import { segmentEmailBody } from "@shared/email-body-segment";
import {
  getEmailPresetTemplate,
  visibleEmailPresetBlocks,
  isEmailBlockDrivenPresetEmail,
  orderEmailBlockDrivenBlocks,
  normalizeEmailBlockDrivenBlocksPreserving,
  EMAIL_PRESET_DEFAULT_BADGES,
  type EmailPresetImage,
  type EmailPresetInputs,
} from "@shared/email-presets";
import {
  renderEmailPresetBlock,
  emailPresetSectionLabel,
  isEmailPresetSectionKey,
} from "@/lib/email-preset-blocks";
import {
  resolveEmailBrandStyle,
  EMAIL_FONT_LINK_HREF,
  EMAIL_TYPE_SCALE,
  EMAIL_BODY_IMAGE_HEIGHT,
  type EmailBrandStyle,
} from "@/lib/email-brand-style";
import {
  EmailEditableText,
  EmailBlockRegenerate,
  EmailLayoutCycle,
  EmailHeroCopyRegenerate,
  EmailBlockImageActions,
  EmailInlineAddButton,
  EmailListItemControls,
  EmailCornerRemove,
  EmailReviewPicker,
  EmailLinkUrlPopover,
  type EmailEditor,
} from "@/components/email-inline-editor";
import {
  Image as ImageIcon,
  Type as TypeIcon,
  PanelBottom,
  ListChecks,
  Star,
  LayoutGrid,
  Swords,
  Columns3,
  Lightbulb,
  Flag,
  Loader2,
  AlertTriangle,
  RefreshCw,
  ShieldCheck,
  BadgeCheck,
  Truck,
  X,
  BadgePercent,
  MousePointerClick,
} from "lucide-react";

export const FLOW_LABELS: Record<string, string> = {
  welcome: "Welcome series",
  cart_abandonment: "Cart abandonment",
  browse_abandonment: "Browse abandonment",
  retention: "Retention–winback",
};

export type EmailDetail = {
  id: string;
  brandId: string;
  name: string;
  type: "campaign" | "flow";
  status: "draft" | "sending" | "send_failed" | "sent_to_klaviyo";
  personaId?: string | null;
  productId?: string | null;
  personaNameSnapshot?: string | null;
  productNameSnapshot?: string | null;
  narrativeAngleLabel?: string | null;
  flowId?: string | null;
  flowType?: string | null;
  flowSequence?: number | null;
  saleDiscountLabel?: string | null;
  saleCode?: string | null;
  salePercentage?: number | null;
  blocks?: string[] | null;
  hiddenBlocks?: string[] | null;
  // Background copy-generation progress (progressive load, like LP Express).
  // `copyStatus` is the whole-email gate (pending → ready/failed); null on
  // legacy emails created before progressive load (treated as fully ready).
  // `blockCopyStatus` maps each block key → its own pending/ready/failed state
  // so the editor can show per-block skeletons while the worker fills them in.
  copyStatus?: "awaiting_template" | "pending" | "ready" | "failed" | null;
  copyError?: string | null;
  blockCopyStatus?: Record<string, "pending" | "ready" | "failed"> | null;
  // The resolved Brand Voice Brief (W1). Persisted once by the copy worker
  // before any downstream stage; used by the client progress model as the first
  // real "stage complete" signal.
  voiceBrief?: string | null;
  subjectLineVariants?: string[] | null;
  previewText?: string | null;
  headline?: string | null;
  bodyCopy?: string | null;
  ctaText?: string | null;
  blockCopy?: EmailBlockCopy | null;
  linkConfig?: EmailLinkConfig | null;
  // Per-email footer edits (social links, disclaimer, unsubscribe URL), merged
  // over the snapshotted Universal footer at render time. Durable; never written
  // by generate/regenerate/copy-worker.
  footerOverrides?: EmailFooterOverrides | null;
  universalAssets?: EmailUniversalAssets | null;
  heroImageUrl?: string | null;
  heroImageStatus?: "pending" | "ready" | "failed" | null;
  // Task #2329 — server-persisted "polished hero is stale after a swap, user
  // must explicitly Apply" flag (never auto-regenerates).
  heroSwapPending?: boolean | null;
  heroImageError?: string | null;
  heroImageStartedAt?: string | null;
  usVsThemImageUrl?: string | null;
  usVsThemImageStatus?: "pending" | "ready" | "failed" | null;
  usVsThemImageError?: string | null;
  usVsThemImageStartedAt?: string | null;
  comparisonTableImageUrl?: string | null;
  comparisonTableImageStatus?: "pending" | "ready" | "failed" | null;
  comparisonTableImageError?: string | null;
  comparisonTableImageStartedAt?: string | null;
  productImageUrls?: string[] | null;
  // One real photo per bundle member SKU for the bundle-only product-list block.
  productListImages?: { name: string; imageUrl: string }[] | null;
  sellingPointsImages?: EmailSellingPointsImage[] | null;
  didYouKnowImages?: EmailDidYouKnowImage[] | null;
  // Polish pipeline (master-only). When polishStatus === "ready", `polishBlocks`
  // is the canonical preview/download/send (a vertical stack of horizontal image
  // blocks). Server-managed; the client only reads these.
  polishStatus?: "idle" | "pending" | "ready" | "failed" | null;
  polishStage?: string | null;
  polishError?: string | null;
  polishStartedAt?: string | null;
  polishBlocks?: EmailPolishBlockClient[] | null;
  // Content fingerprint captured at polish time. The editor recomputes the live
  // hash and compares to flag "edits since last polish" (stale → re-polish).
  polishSourceHash?: string | null;
  // Task #863 scoped-regeneration state. `userEditedFields` records which copy
  // keys the user hand-edited (preserved by "Regenerate content"); `lockedBlocks`
  // / `lockedImages` are explicit user locks; `regenLog` records what each
  // regeneration run did. Server-managed.
  userEditedFields?: string[] | null;
  lockedBlocks?: string[] | null;
  lockedImages?: string[] | null;
  regenLog?: unknown;
  // Per-mode in-flight status while a scoped regeneration runs (one of
  // EMAIL_REGEN_STATUSES); null when idle. Server-managed (lazily cleared).
  regenStatus?: string | null;
  // Preset-template render path (Task #975). When `presetTemplateKey` is set
  // the email renders deterministically from the template's fixed section
  // structure (shared/email-presets.ts) with brand-resolved colors/fonts;
  // `presetData` holds the create-time inputs (myth, awareness facts, second
  // product snapshot…) and `presetImages` the AI image slots keyed by section
  // id. All three are null/absent on archetype emails.
  presetTemplateKey?: string | null;
  presetData?: EmailPresetInputs | null;
  presetImages?: EmailPresetImage[] | null;
};

// Mirror of shared `EmailPolishBlock` (kept local to avoid a server-schema
// import in the client bundle).
export type EmailPolishBlockClient = {
  index: number;
  y: number;
  height: number;
  url: string;
  hasProtectedRegion: boolean;
  alt?: string;
  // Per-block CTA link resolved from the section it covers (multi-link mode);
  // falls back to the email default at send time.
  href?: string;
  // Stable instance key of the block this image renders (e.g. "selling_points__a1b2").
  // Lets the editor map a manifest image to its live block + drive per-block
  // regenerate. Absent on legacy (pre-v12.1) manifests.
  blockKey?: string;
  // Content fingerprint of the copy this image was rendered from. The editor
  // recomputes the live per-block hash and compares to flag a stale block.
  contentHash?: string;
  visualHash?: string;
  // Present on Body Copy entries when the rendered section is known to contain
  // imagery that can be replaced. Missing legacy metadata fails closed.
  hasImagery?: boolean;
  // Set when a per-block regenerate failed: the prior image is kept and the
  // editor shows a localized retry for just this block (never the global card).
  renderError?: string;
  // POLISH_STITCH_VERSION baked in by the stitch stage. When older/absent the
  // editor silently triggers a FREE partial re-stitch (no re-render) so stitch
  // fixes (e.g. tighter edge-band trimming) reach already-polished emails.
  stitchVersion?: string;
};

export type BlockMeta = {
  label: string;
  icon: any;
  locked: boolean;
  regenerable: boolean;
};

export const LOCKED_KEYS = ["hero", "body", "footer"];

export const BLOCK_META: Record<string, BlockMeta> = {
  hero: { label: "Hero + CTA", icon: ImageIcon, locked: true, regenerable: true },
  body: { label: "Body copy", icon: TypeIcon, locked: true, regenerable: true },
  footer: { label: "Footer", icon: PanelBottom, locked: true, regenerable: false },
  selling_points: { label: "Selling points", icon: ListChecks, locked: false, regenerable: true },
  reviews: { label: "Reviews", icon: Star, locked: false, regenerable: true },
  product_list: { label: "Product list", icon: LayoutGrid, locked: false, regenerable: true },
  us_vs_them: { label: "Us vs. them", icon: Swords, locked: false, regenerable: true },
  comparison_table: { label: "Comparison table", icon: Columns3, locked: false, regenerable: true },
  did_you_know: { label: "Did you know", icon: Lightbulb, locked: false, regenerable: true },
  logo_banner: { label: "Logo banner", icon: Flag, locked: false, regenerable: true },
  image_only: { label: "Image only", icon: ImageIcon, locked: false, regenerable: false },
  image_discount: { label: "Image + discount", icon: BadgePercent, locked: false, regenerable: false },
  cta: { label: "CTA button", icon: MousePointerClick, locked: false, regenerable: false },
};

export const OPTIONAL_ORDER = [
  "selling_points",
  "reviews",
  "product_list",
  "us_vs_them",
  "comparison_table",
  "did_you_know",
  "logo_banner",
  "image_only",
  "image_discount",
  "cta",
];

// Optional blocks whose copy comes from an AI (sim.ai) workflow. When one of
// these is added after the email was first generated it starts out empty, so the
// editor auto-runs its workflow on add instead of leaving a placeholder.
// `reviews` is excluded: it is data-driven (seeded from real product reviews),
// not AI-generated.
export const AI_COPY_BLOCKS = [
  "selling_points",
  "product_list",
  "us_vs_them",
  "comparison_table",
  "did_you_know",
  "logo_banner",
];

export const DEFAULT_LINK_CONFIG: EmailLinkConfig = {
  mode: "single",
  sitewideUrl: "",
  blockLinks: {},
};

// Sanitize a string into a safe filename fragment: lowercase, spaces→hyphens,
// strip anything that isn't a-z/0-9/hyphen, collapse repeats, trim edge hyphens.
export function sanitizeFileName(s: string): string {
  return (
    (s || "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9-]+/g, "")
      .replace(/-+/g, "-")
      .replace(/^-|-$/g, "") || "email"
  );
}

// Fetch an image URL and return it as a data: URL. Cross-origin https images are
// routed through the server image-download proxy (same-origin → no CORS block)
// so brand/R2 logos embed into the captured PNG instead of rendering blank.
export async function fetchImageDataUrl(url: string): Promise<string | null> {
  try {
    let fetchUrl = url;
    try {
      const u = new URL(url, window.location.origin);
      if (u.origin !== window.location.origin && u.protocol === "https:") {
        fetchUrl = `/api/img-download?url=${encodeURIComponent(url)}`;
      }
    } catch {
      // Relative/same-origin URL — fetch as-is.
    }
    const res = await fetch(fetchUrl);
    if (!res.ok) return null;
    const blob = await res.blob();
    return await new Promise<string | null>((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

// Shared html-to-image capture options for the preview blocks. Flattens the
// card frame on the capture clone (the live preview keeps its chrome) and drops
// any node flagged `data-export-exclude` (the hover overlay).
export const CAPTURE_OPTS = {
  pixelRatio: 2,
  cacheBust: true,
  backgroundColor: "#ffffff",
  style: {
    borderRadius: "0",
    boxShadow: "none",
  } as Partial<CSSStyleDeclaration>,
  filter: (node: HTMLElement) =>
    !(node?.dataset && node.dataset.exportExclude !== undefined),
};

// Normalize a stored block list to display order: hero, body, optional…, footer.
// Blocks are keyed by INSTANCE id (a bare block type, or "<type>__<suffix>" for
// repeated optional blocks); validity and locked-ness are decided by the type.
// Optional instances dedupe by their full id so the same type can appear twice,
// while locked blocks always collapse to a single canonical hero/body/footer.
export function normalizeBlocks(raw?: string[] | null): string[] {
  const seen = new Set<string>();
  const optional: string[] = [];
  if (Array.isArray(raw)) {
    for (const x of raw) {
      if (typeof x !== "string") continue;
      const type = emailBlockType(x);
      if (!BLOCK_META[type] || LOCKED_KEYS.includes(type)) continue;
      if (seen.has(x)) continue;
      seen.add(x);
      optional.push(x);
    }
  }
  return ["hero", "body", ...optional, "footer"];
}

// Normalize stored hidden keys to valid optional (non-locked) block instance ids.
export function normalizeHidden(raw?: string[] | null): string[] {
  if (!Array.isArray(raw)) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const x of raw) {
    if (typeof x !== "string") continue;
    const type = emailBlockType(x);
    if (!BLOCK_META[type] || LOCKED_KEYS.includes(type)) continue;
    if (seen.has(x)) continue;
    seen.add(x);
    out.push(x);
  }
  return out;
}

// The block keys an email will actually render, in display order, with hidden
// blocks removed — exactly the set the editor captures.
export function visibleBlocksOf(email: EmailDetail): string[] {
  const hidden = normalizeHidden(email.hiddenBlocks);
  // Preset-template emails render the template's sections (the deterministic
  // path — no archetype hero/body/footer scaffolding). The stored `blocks`
  // order is honored for UNLOCKED sections (drag-to-reorder), locked sections
  // stay pinned at their template positions, and hidden blocks are honored —
  // but the locked bookends (header/trust) never hide.
  if (email.presetTemplateKey) {
    // Block-driven template emails (Task #1032) use the STANDARD blocks, but
    // in the fixed Perfect Pair order (logo_banner may lead the hero, so the
    // archetype [hero, body, ...middle, footer] scaffold doesn't apply).
    // Re-derive the fixed order defensively from the stored list — unknown
    // keys drop, mandatory hero/body/footer always render (locked keys can
    // never be hidden), hidden optional blocks are honored.
    if (isEmailBlockDrivenPresetEmail(email)) {
      // The user's stored order is honored (drag-to-reorder); missing
      // mandatory blocks are restored at their template positions.
      return normalizeEmailBlockDrivenBlocksPreserving(
        Array.isArray(email.blocks) ? (email.blocks as string[]) : null,
      ).filter((k) => !hidden.includes(k));
    }
    const presetDef = getEmailPresetTemplate(email.presetTemplateKey);
    if (presetDef) {
      return visibleEmailPresetBlocks(
        presetDef,
        Array.isArray(email.blocks) ? (email.blocks as string[]) : null,
        hidden,
      );
    }
    return [];
  }
  const all = normalizeBlocks(email.blocks).filter((k) => !hidden.includes(k));
  // `all` is [hero, body, ...visible optional, footer] (locked keys are never
  // hidden). Separate any adjacent comparison blocks within the optional middle
  // using the SAME shared helper as the MJML send so the two renderers can't
  // drift, leaving the locked hero/body/footer fixed.
  const middle = separateEmailComparisonBlocks(all.slice(2, all.length - 1));
  return ["hero", "body", ...middle, "footer"];
}

// The hero "sale" line shown in the preview, derived from the email's snapshot.
export function saleTextOf(email: EmailDetail): string {
  return email.saleDiscountLabel
    ? [
        email.saleDiscountLabel,
        email.salePercentage ? `${email.salePercentage}% off` : null,
        email.saleCode ? `Code ${email.saleCode}` : null,
      ]
        .filter(Boolean)
        .join(" · ")
    : "No sale";
}

// Resolve the link a given block should point at. Delegates to the SHARED
// resolver (same one the send worker uses) so client previews/captures and the
// sent email can never disagree: single mode → the email-wide URL everywhere;
// per-CTA mode → CTA blocks (hero/body) use their own URL, everything else
// falls back to the email-wide URL (legacy "multi" per-section URLs honored).
export function linkForBlock(linkConfig: EmailLinkConfig | null | undefined, key: string): string {
  return resolveEmailBlockHref(linkConfig ?? DEFAULT_LINK_CONFIG, key);
}

// =============================================================================
// Preview block wrapper + renderers (styled HTML representation, not an image)
// =============================================================================
export function PreviewBlock({
  blockKey,
  label,
  selected,
  onSelect,
  children,
}: {
  blockKey: string;
  label: string;
  selected: boolean;
  onSelect: () => void;
  children: ReactNode;
}) {
  return (
    <div
      className={`relative group cursor-pointer${selected ? " ring-2 ring-primary ring-inset z-10" : ""}`}
      onClick={onSelect}
      data-testid={`preview-block-${blockKey}`}
    >
      <div
        data-export-exclude
        className="pointer-events-none absolute inset-0 z-10 hidden group-hover:block bg-primary/10"
      >
        <span className="absolute left-2 top-2 rounded bg-primary px-2 py-0.5 text-[10px] font-semibold text-primary-foreground">
          {label}
        </span>
      </div>
      {children}
    </div>
  );
}

// Progressive-load placeholder shown inside the fixed-width preview while the
// background copy worker is still writing a block's copy (blockCopyStatus
// "pending"). Mirrors the LP Express per-section skeleton. Marked
// data-export-exclude so it can never leak into a headless capture.
// A single shimmer block (bar / image / button) — tinted toward the brand color
// via the --shimmer-base / --shimmer-glow CSS vars set on the skeleton wrapper.
function ShimmerBar({
  className,
  style,
}: {
  className?: string;
  style?: CSSProperties;
}) {
  return (
    <div
      className={`email-skeleton-shimmer rounded ${className ?? ""}`}
      style={style}
    />
  );
}

// Per-block-type skeleton body that roughly approximates the real block's
// height + layout (image band, columns, cards, bullet rows…) so the preview
// doesn't visibly jump when the real content fades in.
function SkeletonBody({ blockType }: { blockType: string }) {
  switch (blockType) {
    case "hero":
      return (
        <div className="space-y-3">
          <ShimmerBar className="h-40 w-full" />
          <ShimmerBar className="h-6 w-3/4" />
          <ShimmerBar className="h-3 w-full" />
          <ShimmerBar className="h-3 w-5/6" />
          <ShimmerBar className="mt-2 h-9 w-40 rounded-md" />
        </div>
      );
    case "selling_points":
    case "did_you_know":
      return (
        <div className="space-y-3">
          <ShimmerBar className="h-5 w-1/2" />
          <ShimmerBar className="h-28 w-full" />
          <ShimmerBar className="h-3 w-full" />
          <ShimmerBar className="h-3 w-5/6" />
          <ShimmerBar className="h-3 w-4/6" />
        </div>
      );
    case "us_vs_them":
    case "comparison_table":
      return (
        <div className="space-y-3">
          <ShimmerBar className="h-5 w-1/2" />
          <div className="grid grid-cols-2 gap-3">
            <ShimmerBar className="h-24 w-full" />
            <ShimmerBar className="h-24 w-full" />
            <ShimmerBar className="h-3 w-full" />
            <ShimmerBar className="h-3 w-full" />
            <ShimmerBar className="h-3 w-5/6" />
            <ShimmerBar className="h-3 w-5/6" />
          </div>
        </div>
      );
    case "product_list":
      return (
        <div className="space-y-3">
          <ShimmerBar className="h-4 w-2/3" />
          <div className="grid grid-cols-3 gap-3">
            {[0, 1, 2].map((i) => (
              <div key={i} className="space-y-2">
                <ShimmerBar className="h-24 w-full" />
                <ShimmerBar className="h-3 w-4/5" />
                <ShimmerBar className="h-3 w-1/2" />
              </div>
            ))}
          </div>
        </div>
      );
    case "reviews":
      return (
        <div className="space-y-3">
          <ShimmerBar className="h-4 w-24" />
          {[0, 1].map((i) => (
            <div key={i} className="space-y-2">
              <ShimmerBar className="h-3 w-full" />
              <ShimmerBar className="h-3 w-5/6" />
              <ShimmerBar className="h-3 w-1/3" />
            </div>
          ))}
        </div>
      );
    case "logo_banner":
      return (
        <div className="flex flex-col items-center gap-3">
          <ShimmerBar className="h-8 w-32" />
          <ShimmerBar className="h-3 w-2/3" />
        </div>
      );
    case "footer":
      return (
        <div className="space-y-2">
          <ShimmerBar className="mx-auto h-3 w-1/2" />
          <ShimmerBar className="mx-auto h-3 w-1/3" />
          <ShimmerBar className="mx-auto h-3 w-2/5" />
        </div>
      );
    case "body":
    default:
      return (
        <div className="space-y-3">
          <ShimmerBar className="h-5 w-2/3" />
          <ShimmerBar className="h-3 w-full" />
          <ShimmerBar className="h-3 w-5/6" />
          <ShimmerBar className="h-3 w-4/6" />
        </div>
      );
  }
}

export function EmailBlockSkeleton({
  label,
  blockKey,
  shimmerBase,
  shimmerGlow,
}: {
  label: string;
  blockKey?: string;
  // Brand-adjacent shimmer colors. Default to neutral slate when no brand color
  // is supplied (and on legacy callers).
  shimmerBase?: string;
  shimmerGlow?: string;
}) {
  const blockType = blockKey ? emailBlockType(blockKey) : "body";
  return (
    <div
      data-export-exclude
      className="px-8 py-10 bg-neutral-50"
      data-testid="email-block-skeleton"
      style={
        {
          "--shimmer-base": shimmerBase ?? "#e2e8f0",
          "--shimmer-glow": shimmerGlow ?? "#f1f5f9",
        } as CSSProperties
      }
    >
      <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-neutral-400">
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Generating {label}…
      </div>
      <div className="mt-4">
        <SkeletonBody blockType={blockType} />
      </div>
    </div>
  );
}

// Inline failed state shown when a block's background copy generation failed.
// `onRetry` re-kicks the whole-email copy worker (retry is email-scoped), so it
// is only wired in the editor; the headless/send paths never see it.
export function EmailBlockFailed({
  label,
  message,
  onRetry,
  retrying,
}: {
  label: string;
  message?: string | null;
  onRetry?: () => void;
  retrying?: boolean;
}) {
  return (
    <div
      data-export-exclude
      className="px-8 py-10 bg-red-50 text-center"
      data-testid="email-block-failed"
    >
      <div className="flex items-center justify-center gap-2 text-sm font-semibold text-red-700">
        <AlertTriangle className="h-4 w-4" /> Couldn't generate {label}
      </div>
      {message ? (
        <p className="mt-1 text-xs text-red-600">{message}</p>
      ) : null}
      {onRetry ? (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onRetry();
          }}
          disabled={retrying}
          className="mt-3 inline-flex items-center gap-1.5 rounded-md border border-red-300 bg-white px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-100 disabled:opacity-50"
          data-testid="button-retry-copy"
        >
          {retrying ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <RefreshCw className="h-3.5 w-3.5" />
          )}
          Retry
        </button>
      ) : null}
    </div>
  );
}

export type PreviewCtx = {
  brandName: string;
  headline: string;
  bodyCopy: string;
  ctaText: string;
  blockCopy: EmailBlockCopy;
  sale: string;
  style: EmailBrandStyle;
  footer: UniversalFooterContent | null;
  logoExportUrl?: string | null;
  // The fixed design width the preview renders at (600 desktop / 390 mobile).
  // Comparison grids tighten their padding/font at narrow widths so columns
  // aren't starved — mirrors the sent-email media query. Defaults to desktop.
  designWidth?: number;
  // Hero banner image — the same brand-true photo the send embeds, persisted on
  // the email and rendered behind the headline band so the preview matches the
  // sent email. `heroImageStatus` drives the skeleton (pending) and fallback
  // (failed/missing → solid-color text hero). `heroExportUrl` is a data URL used
  // during PNG export so the cross-origin R2 image isn't blank in the capture.
  heroImageUrl?: string | null;
  heroImageStatus?: string | null;
  heroExportUrl?: string | null;
  // Us-vs-them comparison image — the W7-generated, R2-durable visual rendered
  // above the comparison grid (same image the send embeds). `usVsThemImageStatus`
  // drives the skeleton (pending); a failed/missing image just omits the visual.
  // `usVsThemExportUrl` is a same-origin data URL swapped in during PNG export.
  usVsThemImageUrl?: string | null;
  usVsThemImageStatus?: string | null;
  usVsThemExportUrl?: string | null;
  // Comparison-table supporting image — the W9-generated, R2-durable visual
  // rendered above the comparison grid (same image the send embeds). Mirrors the
  // us-vs-them trio: status drives the skeleton, exportUrl is the PNG-capture swap.
  comparisonTableImageUrl?: string | null;
  comparisonTableImageStatus?: string | null;
  comparisonTableExportUrl?: string | null;
  // Product-list block photos. A "range" block uses `productImageUrls` (the
  // brand's available product photos); a "bundle" block uses `productListImages`
  // (one photo per bundle member SKU). The matching `*ExportUrls` are same-origin
  // data URLs swapped in during PNG export so cross-origin images aren't blank /
  // don't taint the capture (each index-aligned with its own source array).
  productImageUrls?: string[];
  productExportUrls?: string[];
  productImageExportUrls?: string[];
  productListImages?: { name: string; imageUrl: string }[];
  // Per-instance selected images for image_only / image_discount blocks
  // (Task #2286): maps a SOURCE image URL (the instance's chosen product photo
  // or custom upload) to a same-origin data URL swapped in during PNG export
  // so the cross-origin image renders in (and doesn't taint) the capture.
  blockImageExportUrls?: Record<string, string>;
  // Per-instance selling-points supporting images (W5 Selling Points workflow),
  // keyed by block INSTANCE id. The SAME brand-true image the send embeds,
  // persisted on the email so the preview matches the sent email.
  // `sellingPointsImageStatus` drives the skeleton (pending) and the
  // hide-on-failure fallback; `sellingPointsExportUrls` are same-origin data URLs
  // swapped in during PNG export so the cross-origin R2 image isn't blank /
  // doesn't taint the capture.
  sellingPointsImageUrls?: Record<string, string | null>;
  sellingPointsImageStatus?: Record<string, string | null>;
  sellingPointsExportUrls?: Record<string, string>;
  // Did-you-know (W8) supporting image — same status/export contract as
  // selling-points above, keyed by block instance id.
  didYouKnowImageUrls?: Record<string, string | null>;
  didYouKnowImageStatus?: Record<string, string | null>;
  didYouKnowExportUrls?: Record<string, string>;
  // Satori-baked block images keyed like the send path's `rasterizedBlocks`
  // ("hero_band", plus each did_you_know INSTANCE id). Values are base64 PNG
  // data URLs produced by the preview-blocks endpoint so the editor preview
  // shows the SAME pixel-locked typography the sent email embeds. Absent keys
  // fall back to the live-CSS rendering.
  rasterizedBlocks?: Record<string, string>;
  // Preset-template render path (Task #975). `presetTemplateKey`/`presetData`
  // mirror the email row; `presetImageUrls`/`presetImageStatus` are the AI
  // image slots keyed by SECTION id (from email.presetImages) with the usual
  // pending-skeleton / ready / hide-on-failed contract; `presetExportUrls` are
  // same-origin data URLs (keyed by section id) swapped in during PNG capture;
  // `presetUrlExportUrls` maps RAW image URLs (category-grid photos, the
  // second-product snapshot) to capture-safe data URLs.
  presetTemplateKey?: string | null;
  presetData?: EmailPresetInputs | null;
  presetImageUrls?: Record<string, string | null>;
  presetImageStatus?: Record<string, string | null>;
  presetExportUrls?: Record<string, string>;
  presetUrlExportUrls?: Record<string, string>;
  // Task #2556 — per-slot image-treatment export URLs for eligible blocks.
  // Maps a SOURCE image URL (from an imageTreatments "selected" slot) to a
  // same-origin data URL swapped in during PNG export so the chosen image
  // renders in (and doesn't taint) the capture. Keyed by the source URL.
  imageTreatmentExportUrls?: Record<string, string>;
  // In-canvas editing callbacks for every regenerable block (hero, body, CTA,
  // and sections). Present ONLY when the email editor renders the preview (never
  // on the headless send / PNG-export paths, which build their own ctx without
  // it), so the editing chrome below can never leak into the compiled MJML or
  // the captured email image.
  editor?: EmailEditor;
  // Bottom-edge-sampled footer colors for the polished/captured PREVIEW stacks
  // (editor only). The send path samples the bottom of the last stacked block
  // image and paints the appended footer with it; the editor mirrors that by
  // canvas-sampling the same image client-side and passing the result here so
  // the preview footer flows out of the image stack exactly like the sent
  // email. Absent/null (raw block preview, sampling failed) keeps the brand
  // style's page background — the same fallback chain as the send path.
  sampledFooter?: { bg: string; text: string } | null;
};

// Apply ~12% transparency to a hex color (for soft tints on solid surfaces).
function tint(hex: string, alpha: number): string {
  const v = hex.replace("#", "");
  if (v.length !== 6) return hex;
  const r = parseInt(v.slice(0, 2), 16);
  const g = parseInt(v.slice(2, 4), 16);
  const b = parseInt(v.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function BrandCta({ ctx, className }: { ctx: PreviewCtx; className?: string }) {
  const { button } = ctx.style;
  // Mobile-first: full-width, ≥48px tap target (py-4 ≈ 16px each side + ~16px line).
  // Type scale via inline fontSize (dynamic Tailwind arbitrary classes are not
  // JIT-generated, so scale-driven sizes must be styles, not class names).
  const cls = `w-full block text-center px-8 py-4 font-bold uppercase tracking-wider ${className ?? "mt-5"}`;
  // Brand CTA style extras: border (outline fill) + CTA label font. Undefined
  // when unset so legacy brands render byte-identical. (Label already uppercase.)
  const style = {
    backgroundColor: button.backgroundColor,
    color: button.color,
    borderRadius: button.borderRadius,
    border: button.border ?? undefined,
    fontFamily: button.fontFamily ?? ctx.style.fonts.body,
    fontSize: `${EMAIL_TYPE_SCALE.cta}px`,
    minHeight: "48px",
  };
  // In the editor the CTA label is click-to-edit. We render it as a styled
  // (inline-block) div rather than a <button> so the contentEditable label can
  // take focus (interactive content nested in a <button> can't). The send/export
  // path (no ctx.editor) keeps the real <button> with identical look.
  if (ctx.editor) {
    return (
      <div className={cls} style={style} data-testid="preview-cta-button">
        <EmailEditableText
          as="span"
          value={ctx.ctaText ?? ""}
          placeholder="Shop now"
          onCommit={ctx.editor.onEditCtaText}
          testId="editable-cta-text"
        />
      </div>
    );
  }
  return (
    <button className={cls} style={style} data-testid="preview-cta-button">
      {ctx.ctaText || "Shop now"}
    </button>
  );
}

// The sale as a compact accent pill (matches the MJML hero badge).
function SalePill({ ctx }: { ctx: PreviewCtx }) {
  if (!ctx.sale || ctx.sale === "No sale") return null;
  const { colors, fonts } = ctx.style;
  return (
    <div className="mt-4">
      <span
        className="inline-block rounded-full px-4 py-1.5 font-bold uppercase tracking-wider"
        style={{ backgroundColor: colors.accent, color: colors.onAccent, fontFamily: fonts.body, fontSize: `${EMAIL_TYPE_SCALE.eyebrow}px` }}
      >
        {ctx.sale}
      </span>
    </div>
  );
}

function BrandLogo({
  ctx,
  onDark,
  size = "default",
}: {
  ctx: PreviewCtx;
  onDark?: boolean;
  // "footer" renders a larger logo/wordmark for the footer's brand sign-off.
  size?: "default" | "footer";
}) {
  if (ctx.style.logoUrl) {
    return (
      <img
        src={ctx.logoExportUrl || ctx.style.logoUrl}
        alt={ctx.brandName}
        className={`mx-auto mb-3 ${size === "footer" ? "h-12" : "h-9"} w-auto object-contain`}
        data-testid="preview-logo"
      />
    );
  }
  // No logo on file: degrade to the brand name as a wordmark.
  return (
    <div
      className={`mb-3 font-semibold uppercase ${size === "footer" ? "tracking-[0.16em]" : "tracking-[0.2em]"}`}
      style={{
        color: onDark ? "rgba(255,255,255,0.85)" : ctx.style.colors.text,
        fontFamily: ctx.style.fonts.heading,
        // Footer wordmark matches the MJML/Satori footer wordmark step; the
        // inline (eyebrow-sized) variant sits on the label step.
        fontSize: size === "footer" ? EMAIL_TYPE_SCALE.wordmarkFooter : EMAIL_TYPE_SCALE.label,
      }}
      data-testid="preview-wordmark"
    >
      {ctx.brandName}
    </div>
  );
}

// Editorial section intro: an uppercase kicker with a short accent underline
// beneath it (mirrors the MJML sectionIntro treatment).
function SectionHeading({
  text,
  ctx,
  onEdit,
  headingColor,
  ruleColor,
}: {
  text?: string;
  ctx: PreviewCtx;
  onEdit?: (value: string) => void;
  // Override the heading text / underline-rule colors (default: brand text +
  // accent). Used on a saturated band where an accent rule would vanish.
  headingColor?: string;
  ruleColor?: string;
}) {
  // WYSIWYG-strict: only render (and thus only make editable) a heading that
  // actually shows in the sent email. An empty punchline stays panel-only.
  if (!text || !text.trim()) return null;
  const { colors, fonts } = ctx.style;
  const headingCls = "text-[13px] font-bold uppercase tracking-[0.12em]";
  const headingStyle = { color: headingColor ?? colors.text, fontFamily: fonts.heading };
  return (
    <div className="text-center mb-5" data-testid="preview-section-heading">
      {ctx.editor && onEdit ? (
        <EmailEditableText
          as="div"
          value={text}
          onCommit={onEdit}
          className={`${headingCls} inline-block`}
          style={headingStyle}
          testId="editable-section-heading"
        />
      ) : (
        <div className={headingCls} style={headingStyle}>
          {text}
        </div>
      )}
      <div
        className="mx-auto mt-2.5 h-[3px] w-11 rounded-full"
        style={{ backgroundColor: ruleColor ?? colors.accent }}
      />
    </div>
  );
}

// The hero headline: a plain <h2> on the send/export paths; a click-to-edit
// field in the editor (ctx.editor present). Same tag/class/style in both so the
// editable preview matches the sent email pixel-for-pixel.
function HeroHeadline({
  ctx,
  className,
  style,
}: {
  ctx: PreviewCtx;
  className: string;
  style: React.CSSProperties;
}): ReactNode {
  if (ctx.editor) {
    return (
      <EmailEditableText
        as="h2"
        value={ctx.headline ?? ""}
        placeholder="Your headline appears here"
        multiline
        onCommit={ctx.editor.onEditHeadline}
        className={className}
        style={style}
        testId="editable-hero-headline"
      />
    );
  }
  return (
    <h2 className={className} style={style}>
      {ctx.headline || "Your headline appears here"}
    </h2>
  );
}

// Hover-revealed in-canvas editing chrome for the hero: the copy-regenerate
// pill bottom-left (and the layout-cycle pill on non-block-driven previews).
// Image actions (regenerate / upload / choose) deliberately do NOT render
// in-canvas — the right-sidebar edit-panel picker is the sole hero image
// surface (Task #2329, per user request). Rendered ONLY when ctx.editor is
// present, and every node is also marked data-export-exclude so it can never
// reach the captured email image.
function HeroEditChrome({ ctx, blockKey }: { ctx: PreviewCtx; blockKey: string }): ReactNode {
  const editor = ctx.editor;
  if (!editor) return null;
  return (
    <div
      className="pointer-events-none absolute inset-0 z-30 transition-opacity focus-within:opacity-100 opacity-0 group-hover/herochrome:opacity-100"
      data-export-exclude
    >
      <div className="pointer-events-auto absolute bottom-2 left-2">
        <EmailHeroCopyRegenerate
          onRegenerate={editor.onRegenerateHeroCopy}
          isRegenerating={editor.isRegeneratingHeroCopy}
        />
      </div>
      {!isBlockDrivenPreviewCtx(ctx) && (
        <div className="pointer-events-auto absolute bottom-2 right-2">
          <EmailLayoutCycle onChange={() => editor.onChangeLayout(blockKey)} tone="dark" />
        </div>
      )}
    </div>
  );
}

// Inline field that is click-to-edit when the editor is present and plain
// text/markup otherwise — SAME tag/class/style in both, so the editable preview
// matches the sent email. `children` overrides the read-mode content (used when
// the field is wrapped in decoration like quotes).
function Editable({
  ctx,
  as = "span",
  value,
  placeholder,
  multiline,
  className,
  style,
  onCommit,
  testId,
  children,
}: {
  ctx: PreviewCtx;
  as?: keyof JSX.IntrinsicElements;
  value: string;
  placeholder?: string;
  multiline?: boolean;
  className?: string;
  style?: React.CSSProperties;
  onCommit: (value: string) => void;
  testId?: string;
  children?: ReactNode;
}): ReactNode {
  if (ctx.editor) {
    return (
      <EmailEditableText
        as={as}
        value={value}
        placeholder={placeholder}
        multiline={multiline}
        className={className}
        style={style}
        onCommit={onCommit}
        testId={testId}
      />
    );
  }
  return createElement(
    as,
    { className, style, "data-testid": testId },
    children ?? (value || placeholder || ""),
  );
}

// Small all-caps "eyebrow" kicker shown above a block's headline/section intro,
// but ONLY when the assigned layout variant calls for it (emailVariantShowsEyebrow).
// Decorative and sim-generated: it renders only when it actually has text. An empty
// eyebrow is never shown — not even an editor placeholder — so switching layouts can
// never surface a "Eyebrow kicker" placeholder (editor and send path stay in lockstep).
function EmailEyebrow({
  ctx,
  value,
  color,
  align = "center",
  onCommit,
  testId,
}: {
  ctx: PreviewCtx;
  value: string;
  color: string;
  align?: "center" | "left";
  onCommit?: (v: string) => void;
  testId?: string;
}): ReactNode {
  const { fonts } = ctx.style;
  if (!value.trim()) return null;
  const cls = "font-bold uppercase tracking-[0.18em]";
  const style: CSSProperties = { color, fontFamily: fonts.heading, fontSize: `${EMAIL_TYPE_SCALE.eyebrow}px` };
  return (
    <div className={`mb-2 ${align === "center" ? "text-center" : "text-left"}`}>
      {ctx.editor && onCommit ? (
        <EmailEditableText
          as="div"
          value={value}
          placeholder="Eyebrow kicker"
          onCommit={onCommit}
          className={`${cls} inline-block`}
          style={style}
          testId={testId}
        />
      ) : (
        <div className={cls} style={style} data-testid={testId}>
          {value}
        </div>
      )}
    </div>
  );
}

// Short, visually distinct italic "pull quote" line. Used by the HERO (rendered
// as a standalone italic line under the headline) when the variant calls for it.
// Body Copy uses its OWN in-place verbatim highlight instead of this component.
function EmailPullQuote({
  ctx,
  value,
  color,
  onCommit,
  testId,
}: {
  ctx: PreviewCtx;
  value: string;
  color: string;
  onCommit?: (v: string) => void;
  testId?: string;
}): ReactNode {
  const { fonts } = ctx.style;
  if (!value.trim()) return null;
  const cls = "mx-auto mt-4 max-w-[90%] text-[16px] italic leading-[1.5]";
  const style: CSSProperties = { color, fontFamily: fonts.heading };
  return ctx.editor && onCommit ? (
    <EmailEditableText
      as="p"
      value={value}
      placeholder="Pull quote"
      multiline
      onCommit={onCommit}
      className={cls}
      style={style}
      testId={testId}
    />
  ) : (
    <p className={cls} style={style} data-testid={testId}>
      {value}
    </p>
  );
}

// Functional offer callout (HERO only): a label, a prominently displayed promo
// code, a description, and a brand-colored CTA. Rendered whenever the offer box
// is non-null REGARDLESS of the hero variant. In the editor every field is
// click-to-edit; on the send/export path the CTA is a styled button (the live
// href is wired in the MJML send renderer).
function EmailOfferBox({
  ctx,
  box,
  blockKey,
}: {
  ctx: PreviewCtx;
  box: { label: string; code: string; description: string; cta: string };
  blockKey: string;
}): ReactNode {
  const { colors, fonts, button } = ctx.style;
  const editor = ctx.editor;
  const patch = (
    partial: Partial<{ label: string; code: string; description: string; cta: string }>,
  ) => editor?.onPatchInstanceCopy(blockKey, { offerBox: { ...box, ...partial } });
  const ctaCls =
    "mt-3 inline-block rounded px-6 py-2.5 text-[13px] font-bold uppercase tracking-wider";
  const ctaStyle: CSSProperties = {
    backgroundColor: button.backgroundColor,
    color: button.color,
    borderRadius: button.borderRadius,
    border: button.border ?? undefined,
    fontFamily: button.fontFamily ?? fonts.body,
  };
  return (
    <div
      className="mx-auto mt-6 max-w-[92%] rounded-xl px-5 py-4 text-center"
      style={{
        backgroundColor: "rgba(255,255,255,0.12)",
        border: "1px solid rgba(255,255,255,0.35)",
        fontFamily: fonts.body,
      }}
      data-testid="preview-offer-box"
    >
      {(box.label.trim() || editor) && (
        <Editable
          ctx={ctx}
          as="div"
          value={box.label}
          placeholder="Offer label"
          onCommit={(v) => patch({ label: v })}
          className="text-[11px] font-bold uppercase tracking-[0.16em]"
          style={{ color: colors.onPrimary, fontFamily: fonts.heading }}
          testId="editable-offer-label"
        />
      )}
      {(box.code.trim() || editor) && (
        <Editable
          ctx={ctx}
          as="div"
          value={box.code}
          placeholder="CODE"
          onCommit={(v) => patch({ code: v })}
          className="my-2 inline-block rounded-md px-3 py-1.5 text-[18px] font-extrabold tracking-[0.12em]"
          style={{ backgroundColor: colors.accent, color: colors.onAccent, fontFamily: fonts.heading }}
          testId="editable-offer-code"
        />
      )}
      {(box.description.trim() || editor) && (
        <Editable
          ctx={ctx}
          as="p"
          value={box.description}
          placeholder="Offer description"
          multiline
          onCommit={(v) => patch({ description: v })}
          className="text-[13px] leading-[1.5]"
          style={{ color: colors.onPrimary, opacity: 0.92 }}
          testId="editable-offer-description"
        />
      )}
      {(box.cta.trim() || editor) &&
        (editor ? (
          <div className={ctaCls} style={ctaStyle}>
            <Editable
              ctx={ctx}
              as="span"
              value={box.cta}
              placeholder="Claim offer"
              onCommit={(v) => patch({ cta: v })}
              testId="editable-offer-cta"
            />
          </div>
        ) : (
          <button className={ctaCls} style={ctaStyle} data-testid="button-offer-cta">
            {box.cta || "Claim offer"}
          </button>
        ))}
    </div>
  );
}

// Hover-revealed one-click "Regenerate" pill for a non-hero regenerable block,
// pinned top-right. Rendered ONLY when ctx.editor is present and the block type
// is regenerable, and marked data-export-exclude so it never reaches the
// captured email image. The containing block must be `group/blockchrome relative`.
function BlockEditChrome({
  ctx,
  blockKey,
  tone = "light",
}: {
  ctx: PreviewCtx;
  blockKey: string;
  tone?: "light" | "dark";
}): ReactNode {
  const editor = ctx.editor;
  if (!editor) return null;
  const type = emailBlockType(blockKey);
  const regenerable = !!BLOCK_META[type]?.regenerable;
  // A block can change layout when its type has more than one catalog variant.
  // Block-driven template emails (Task #1032) have a FIXED layout per block —
  // never offer the layout cycle there.
  const canChangeLayout =
    !isBlockDrivenPreviewCtx(ctx) && emailLayoutVariantsFor(type).length > 1;
  if (!regenerable && !canChangeLayout) return null;
  return (
    <div
      className="pointer-events-none absolute right-2 top-2 z-30 flex items-center gap-1.5 opacity-0 transition-opacity group-hover/blockchrome:opacity-100 focus-within:opacity-100"
      data-export-exclude
    >
      {canChangeLayout ? (
        <div className="pointer-events-auto">
          <EmailLayoutCycle onChange={() => editor.onChangeLayout(blockKey)} tone={tone} />
        </div>
      ) : null}
      {regenerable ? (
        <div className="pointer-events-auto">
          <EmailBlockRegenerate
            onRegenerate={(feedback) => editor.onRegenerateBlock(blockKey, feedback)}
            isRegenerating={editor.regeneratingBlockKey === blockKey}
            tone={tone}
          />
        </div>
      ) : null}
    </div>
  );
}

// Hover-revealed picker chrome for the image_only / image_discount blocks
// (Task #2286): choose one of the product's photos, upload a custom image, or
// reset to the default first product photo. Editor-only (ctx.editor gated) and
// data-export-exclude'd, so it can never reach the captured/sent email.
// Choosing/removing writes a plain instance-copy patch (autosaved) — it never
// triggers polish or any regeneration.
function ImageBlockChrome({
  ctx,
  blockKey,
  hasSelection,
}: {
  ctx: PreviewCtx;
  blockKey: string;
  hasSelection: boolean;
}): ReactNode {
  const editor = ctx.editor;
  if (!editor) return null;
  return (
    <div
      className="pointer-events-none absolute right-2 top-2 z-30 opacity-0 transition-opacity group-hover/blockchrome:opacity-100 focus-within:opacity-100"
      data-export-exclude
    >
      <div className="pointer-events-auto">
        <EmailBlockImageActions
          onUpload={(file) => editor.onUploadBlockImage?.(blockKey, file)}
          onChoose={(url) => editor.onPatchInstanceCopy(blockKey, { imageUrl: url })}
          onReset={() => editor.onPatchInstanceCopy(blockKey, { imageUrl: undefined })}
          hasSelection={hasSelection}
          photos={editor.productPhotos}
          busy={editor.blockImageBusyKey === blockKey}
        />
      </div>
    </div>
  );
}

// Is the given preview ctx a block-driven ("Perfect Pair") template email?
// Requires BOTH the template key and the persisted presetData marker so legacy
// preset emails (and freeform emails) never take this branch — mirrors
// isEmailBlockDrivenPresetEmail for EmailDetail rows.
function isBlockDrivenPreviewCtx(ctx: PreviewCtx): boolean {
  return isEmailBlockDrivenPresetEmail({
    presetTemplateKey: ctx.presetTemplateKey ?? null,
    presetData: ctx.presetData ?? null,
  });
}

// Perfect Pair repeats the brand CTA after every MAJOR content section (the
// hero and body carry their own CTAs already; logo_banner / did_you_know /
// footer never get one).
const BLOCK_DRIVEN_CTA_AFTER: ReadonlySet<string> = new Set([
  "selling_points",
  "us_vs_them",
  "comparison_table",
  "reviews",
  "product_list",
]);

// Perfect Pair footer badge icons, index-aligned with
// EMAIL_PRESET_DEFAULT_BADGES (secure checkout / satisfaction / shipping).
const BLOCK_DRIVEN_BADGE_ICONS = [ShieldCheck, BadgeCheck, Truck] as const;

export function renderPreviewBlock(key: string, ctx: PreviewCtx): ReactNode {
  // Preset-template sections short-circuit to their own deterministic
  // renderers (email-preset-blocks.tsx) — none of the archetype instance-copy/
  // variant machinery below applies to them.
  if (isEmailPresetSectionKey(key)) {
    return renderEmailPresetBlock(key, ctx);
  }
  const node = renderStandardPreviewBlock(key, ctx);
  // Block-driven ("Perfect Pair") structural chrome (Task #1032): the standard
  // block renders unchanged, then the wrapper appends the repeated brand CTA
  // after each major section and prepends the icon-badge row above the footer.
  // Freeform emails return the standard node untouched (byte-identical).
  if (!isBlockDrivenPreviewCtx(ctx)) return node;
  const type = emailBlockType(key);
  const { colors, fonts, surfaces } = ctx.style;
  if (BLOCK_DRIVEN_CTA_AFTER.has(type)) {
    return (
      <>
        {node}
        <div
          className="px-6 pb-7 pt-1"
          style={{ backgroundColor: surfaces.page.bg }}
          data-testid={`block-driven-cta-${key}`}
        >
          <BrandCta ctx={ctx} className="mt-0" />
        </div>
      </>
    );
  }
  // Footer: no badge row anymore — every email closes with the same simple
  // brand-colored footer (brand name + address + unsubscribe) on all surfaces.
  return node;
}

function renderStandardPreviewBlock(key: string, ctx: PreviewCtx): ReactNode {
  const { colors, fonts } = ctx.style;
  const editor = ctx.editor;
  // `key` is a block INSTANCE id; resolve its type (render shape) and its own
  // per-instance copy + punchline so the same type can appear more than once.
  const type = emailBlockType(key);
  const instance: EmailInstanceCopy = resolveEmailInstanceCopy(ctx.blockCopy, key);
  const punchline = ctx.blockCopy.punchlines?.[key];
  // The block's assigned layout variant (visual expression only — copy is
  // identical across variants). `variantNeedsImage` is the cross-surface contract
  // shared with email-mjml.ts so the preview and the sent email always agree on
  // whether an image-bearing block actually renders its generated photo.
  const variant = resolveEmailLayoutVariant(type, instance);
  const variantNeedsImage = emailVariantNeedsImage(type, variant);
  // WCAG-safe brand surface for this block (page/light/tinted/dark/accent). Dark
  // variants resolve to the dark surface so light-on-dark stays legible; mirrors
  // the send renderer's `surf` (email-mjml.ts) for preview↔send parity.
  const surfaceKey = emailSectionSurfaceKey(type, variant);
  const surf = ctx.style.surfaces[surfaceKey];
  // Eyebrow kicker for the NON-hero content sections (selling_points, us_vs_them,
  // comparison_table, did_you_know, product_list). Surfaces only when the assigned
  // variant calls for it; rendered above the section intro in the accent color.
  // (The hero builds its own onPrimary eyebrow above.)
  const sectionEyebrowNode = emailVariantShowsEyebrow(type, variant) ? (
    <EmailEyebrow
      ctx={ctx}
      value={instance.eyebrow ?? ""}
      color={colors.accent}
      onCommit={(v) => editor?.onPatchInstanceCopy(key, { eyebrow: v })}
      testId="editable-section-eyebrow"
    />
  ) : null;
  switch (type) {
    case "logo_banner": {
      // The banner shows the brand logo image (from brand assets) above the
      // AI-generated tagline — matching the sent email (email-mjml.ts). With no
      // logo on file we degrade to the tagline/brand-name text alone.
      const bannerLogoSrc = ctx.logoExportUrl || ctx.style.logoUrl;
      // Three visual treatments (no generated image — the logo is a brand asset):
      //  • centered-minimal — quiet footer-tone strip, small uppercase tagline.
      //  • color-band       — bold accent band with an underline accent rule.
      //  • editorial-type   — light card with a larger heading-font tagline.
      const bannerBg =
        variant === "color-band"
          ? colors.primary
          : variant === "editorial-type"
            ? colors.card
            : colors.footer;
      const bannerFg =
        variant === "color-band"
          ? colors.onPrimary
          : variant === "editorial-type"
            ? colors.text
            : colors.onFooter;
      const bannerTone: "light" | "dark" = variant === "editorial-type" ? "light" : "dark";
      const taglineCls =
        variant === "editorial-type"
          ? "text-[15px] font-semibold tracking-tight"
          : "text-[11px] font-semibold uppercase tracking-[0.15em]";
      return (
        <div
          className="group/blockchrome relative text-center pt-6 pb-3.5 px-4"
          style={{ backgroundColor: bannerBg, color: bannerFg, fontFamily: fonts.body }}
        >
          {bannerLogoSrc ? (
            <img
              src={bannerLogoSrc}
              alt={ctx.brandName}
              className="mx-auto mb-1.5 h-7 w-auto object-contain"
              data-testid="img-logo-banner"
            />
          ) : null}
          {variant === "color-band" && (
            <div
              className="mx-auto mb-2 h-[2px] w-9 rounded-full"
              style={{ backgroundColor: colors.onPrimary, opacity: 0.55 }}
              aria-hidden
            />
          )}
          <Editable
            ctx={ctx}
            as="span"
            className={taglineCls}
            style={variant === "editorial-type" ? { fontFamily: fonts.heading } : undefined}
            value={instance.logoBannerTagline || ""}
            placeholder={ctx.brandName}
            onCommit={(v) => editor?.onPatchInstanceCopy(key, { logoBannerTagline: v })}
            testId="editable-logo-banner-tagline"
          >
            {instance.logoBannerTagline || ctx.brandName}
          </Editable>
          {variant === "editorial-type" && (
            <div
              className="mx-auto mt-2 h-[3px] w-10 rounded-full"
              style={{ backgroundColor: colors.accent }}
              aria-hidden
            />
          )}
          <BlockEditChrome ctx={ctx} blockKey={key} tone={bannerTone} />
        </div>
      );
    }
    case "hero": {
      // The hero matches the sent email's <mj-hero>: when a brand-true photo is
      // ready it sits behind the logo/headline/sale/CTA, with the headline in a
      // translucent brand-color band (tint(primary, .82)) — the same overlay the
      // MJML uses. While the photo is still generating we show a skeleton over
      // the brand color; when it failed or is unavailable we degrade to the
      // solid-color text hero (the MJML's own no-image fallback).
      // Follow the SAME status contract the send path uses
      // (ensureEmailHeroReady): only a `ready` hero shows the photo. A `pending`
      // regen shows the skeleton below (not the stale previous image), and a
      // `failed`/missing hero degrades to the solid-color text hero — so the
      // preview and the sent email never diverge on the hero.
      // `text-dominant` is image-free: it never shows the photo (and the send
      // path skips generating it). Every other variant uses the photo when ready.
      const heroReady = ctx.heroImageStatus === "ready";
      const heroSrc =
        variantNeedsImage && heroReady ? ctx.heroExportUrl || ctx.heroImageUrl : null;
      // Decorative eyebrow / pull quote surface only when the assigned hero
      // variant calls for them (emailVariantShows*); the offer box is functional
      // and shows whenever it's non-null, regardless of variant. All three render
      // light-on-dark over the brand hero.
      const heroEyebrowNode = emailVariantShowsEyebrow("hero", variant) ? (
        <EmailEyebrow
          ctx={ctx}
          value={instance.eyebrow ?? ""}
          color={colors.accent}
          onCommit={(v) => editor?.onPatchInstanceCopy(key, { eyebrow: v })}
          testId="editable-hero-eyebrow"
        />
      ) : null;
      const heroPullNode = emailVariantShowsPullQuote("hero", variant) ? (
        <EmailPullQuote
          ctx={ctx}
          value={instance.pullQuote ?? ""}
          color={colors.text}
          onCommit={(v) => editor?.onPatchInstanceCopy(key, { pullQuote: v })}
          testId="editable-hero-pullquote"
        />
      ) : null;
      const heroOffer = instance.offerBox;
      const heroOfferNode =
        heroOffer &&
        (heroOffer.label || heroOffer.code || heroOffer.description || heroOffer.cta) ? (
          <EmailOfferBox ctx={ctx} box={heroOffer} blockKey={key} />
        ) : null;
      // POLISH DIRECTION (task #724): every photo hero variant renders ONE
      // consistent CREAM hero — a large dark headline commanding the top, the
      // product photo CONTAINED in its own rounded block below (never a
      // full-bleed background bleeding behind text), all on the SAME warm cream
      // surface as the rest of the email. The Satori headline band (white text
      // on a transparent strip, designed to sit over a dark photo) is
      // intentionally bypassed here — it is unreadable on cream. The hero is not
      // a protected region, so AI polish still enhances this layout afterwards.
      //
      // EXCEPTION — block-driven ("Perfect Pair") template emails (task #1032):
      // the reference template's hero is a full-bleed photo with the logo,
      // headline, and CTA composed OVER the image. Those emails pin the hero
      // variant to `image-overlay` and render the overlay composition below
      // (photo as the background, content overlaid on a dark gradient scrim
      // for legibility). The polish prompt carries the matching FIXED overlay
      // direction, so draft preview and polished output agree on structure.
      const heroCream = ctx.style.surfaces.page;
      if (heroSrc && isBlockDrivenPreviewCtx(ctx)) {
        return (
          <div
            className="group/herochrome relative overflow-hidden"
            style={{ backgroundColor: colors.primary, color: "#ffffff" }}
            data-testid="preview-hero-image"
          >
            <img
              src={heroSrc}
              alt={ctx.headline || "Hero"}
              className="absolute inset-0 h-full w-full object-cover"
              data-testid="preview-hero-photo"
            />
            <div
              className="absolute inset-0"
              style={{
                background:
                  "linear-gradient(to bottom, rgba(0,0,0,0.45), rgba(0,0,0,0.18) 40%, rgba(0,0,0,0.5))",
              }}
            />
            <div className="relative px-7 pt-9 pb-10 text-center">
              <BrandLogo ctx={ctx} onDark />
              {heroEyebrowNode}
              <HeroHeadline
                ctx={ctx}
                className="font-extrabold leading-[1.1] tracking-tight whitespace-pre-line"
                style={{
                  fontSize: `${EMAIL_TYPE_SCALE.heroDisplay}px`,
                  fontFamily: fonts.heading,
                  color: "#ffffff",
                  textShadow: "0 2px 14px rgba(0,0,0,0.45)",
                }}
              />
              {/* Open photo window between the headline and the CTA so the
                  full-bleed photograph reads as the hero's background (mirrors
                  the reference template's tall photo hero). */}
              <div className="h-44" aria-hidden="true" />
              {heroPullNode}
              <SalePill ctx={ctx} />
              <BrandCta ctx={ctx} className="mt-5" />
              {heroOfferNode}
            </div>
            <HeroEditChrome ctx={ctx} blockKey={key} />
          </div>
        );
      }
      if (heroSrc) {
        return (
          <div
            className="group/herochrome relative overflow-hidden"
            style={{ backgroundColor: heroCream.bg, color: heroCream.text }}
            data-testid="preview-hero-image"
          >
            <div className="px-7 pt-8 pb-4 text-center">
              <BrandLogo ctx={ctx} />
              {heroEyebrowNode}
              <HeroHeadline
                ctx={ctx}
                className="font-extrabold leading-[1.1] tracking-tight whitespace-pre-line"
                style={{ fontSize: `${EMAIL_TYPE_SCALE.heroDisplay}px`, fontFamily: fonts.heading, color: heroCream.text }}
              />
            </div>
            <div className="px-7 pb-6">
              <img
                src={heroSrc}
                alt={ctx.headline || "Hero"}
                className="mx-auto block w-full rounded-xl"
                style={{ maxHeight: 420, objectFit: "contain", backgroundColor: heroCream.bg }}
                data-testid="preview-hero-photo"
              />
            </div>
            <div className="px-7 pb-10 text-center">
              {heroPullNode}
              <SalePill ctx={ctx} />
              <BrandCta ctx={ctx} className="mt-5" />
              {heroOfferNode}
            </div>
            <HeroEditChrome ctx={ctx} blockKey={key} />
          </div>
        );
      }
      // No-image hero (the `text-dominant` variant, which is image-free by
      // design, plus the graceful fallback while a photo is pending/failed):
      // the SAME warm cream surface + live dark typography as the photo hero
      // above. The Satori band is bypassed (white-on-transparent is unreadable
      // on cream), so even the no-image hero stays consistent with the email.
      const heroPending = variantNeedsImage && ctx.heroImageStatus === "pending";
      const typeStatement = variant === "text-dominant";
      return (
        <div
          className="group/herochrome relative overflow-hidden text-center px-7 py-12"
          style={{ backgroundColor: heroCream.bg, color: heroCream.text }}
          data-testid="preview-hero-fallback"
        >
          {heroPending && (
            <div
              data-export-exclude
              className="absolute inset-0 z-20 flex items-center justify-center"
              style={{ backgroundColor: "rgba(0,0,0,0.06)" }}
            >
              <span
                className="inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-[11px] font-semibold"
                style={{ backgroundColor: colors.card, color: colors.text }}
                data-testid="status-hero-generating"
              >
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Generating hero image…
              </span>
            </div>
          )}
          <BrandLogo ctx={ctx} />
          {heroEyebrowNode}
          <HeroHeadline
            ctx={ctx}
            className={`${typeStatement ? "leading-[1.08]" : "leading-[1.16]"} font-extrabold tracking-tight whitespace-pre-line`}
            style={{
              fontSize: `${typeStatement ? EMAIL_TYPE_SCALE.heroDisplay : EMAIL_TYPE_SCALE.heroHeadline}px`,
              fontFamily: fonts.heading,
              color: heroCream.text,
            }}
          />
          {typeStatement && (
            <div
              className="mx-auto mt-4 h-[3px] w-14 rounded-full"
              style={{ backgroundColor: colors.accent }}
              aria-hidden
            />
          )}
          {heroPullNode}
          <SalePill ctx={ctx} />
          <BrandCta ctx={ctx} className="mt-6" />
          {heroOfferNode}
          <HeroEditChrome ctx={ctx} blockKey={key} />
        </div>
      );
    }
    case "body": {
      const paras = segmentEmailBody(ctx.bodyCopy || "");
      const bodyImage = resolveEmailBodyImagePreference(instance);
      const bodyImageUrl =
        bodyImage.mode === "selected"
          ? ctx.blockImageExportUrls?.[bodyImage.url] || bodyImage.url
          : "";
      // Commit a single paragraph edit back into the joined body copy (editing
      // per-paragraph preserves the styled multi-paragraph layout and avoids the
      // contentEditable newline-loss a single whole-body field would suffer).
      const commitPara = (i: number, val: string) => {
        const next = [...paras];
        next[i] = val;
        editor?.onEditBodyCopy(next.filter((p) => p.trim() !== "").join("\n\n"));
      };
      // Body carries no generated image; the four variants restyle typography and
      // surface (mirrors email-mjml.ts):
      //  • standard           — soft paper, left-aligned, emphasized first paragraph.
      //  • editorial-margins  — oversized lead paragraph with an accent rule.
      //  • pull-quote-feature — centered serif statement on a quiet tint, sparing italic.
      //  • color-band         — light text on the dark brand band (guaranteed depth/contrast).
      // All text comes from `surf` so the dark band stays WCAG-legible.
      const leadin = variant === "editorial-margins";
      const pullQuote = variant === "pull-quote-feature";
      // Body pull quote is NOT a separate line: when the assigned variant calls
      // for it, the pull-quote sentence is highlighted verbatim IN PLACE inside the
      // existing body copy (first case-insensitive match only). Read/export-mode
      // only — the editor keeps paragraphs plain text so editing stays intact.
      const bodyPull = (instance.pullQuote ?? "").trim();
      const showBodyPull = emailVariantShowsPullQuote("body", variant) && !!bodyPull;
      let pullHighlighted = false;
      const highlightPara = (p: string): ReactNode => {
        if (ctx.editor || !showBodyPull || pullHighlighted) return undefined;
        const idx = p.toLowerCase().indexOf(bodyPull.toLowerCase());
        if (idx === -1) return undefined;
        pullHighlighted = true;
        return (
          <>
            {p.slice(0, idx)}
            <em style={{ fontStyle: "italic", fontWeight: 600, color: colors.accent }}>
              {p.slice(idx, idx + bodyPull.length)}
            </em>
            {p.slice(idx + bodyPull.length)}
          </>
        );
      };
      const paraCls = (i: number) => {
        const lead = i === 0;
        // Weight/italic stay as classes; the SIZE comes from the shared type
        // scale via paraStyle (dynamic Tailwind classes are not JIT-generated).
        const emphasis = pullQuote
          ? lead
            ? "font-semibold italic"
            : ""
          : leadin && lead
            ? "font-semibold"
            : lead
              ? "font-medium"
              : "";
        return `leading-[1.7] ${emphasis} ${pullQuote ? "text-center" : ""} ${i > 0 ? "mt-3.5" : ""}`;
      };
      const paraStyle = (i: number): CSSProperties => {
        const lead = i === 0;
        const fontSize = pullQuote
          ? lead
            ? EMAIL_TYPE_SCALE.leadEmphasis
            : EMAIL_TYPE_SCALE.bodyLarge
          : leadin && lead
            ? EMAIL_TYPE_SCALE.leadEmphasis
            : lead
              ? EMAIL_TYPE_SCALE.lead
              : EMAIL_TYPE_SCALE.body;
        const base: CSSProperties = {
          whiteSpace: "pre-line",
          color: surf.text,
          fontSize: `${fontSize}px`,
          fontFamily: pullQuote && i === 0 ? fonts.heading : fonts.body,
        };
        if (leadin && i === 0) {
          return { ...base, borderLeft: `3px solid ${colors.accent}`, paddingLeft: "16px" };
        }
        return base;
      };
      const bodyInner = paras.length ? (
        paras.map((p, i) => (
          <Editable
            key={i}
            ctx={ctx}
            as="p"
            value={p}
            multiline
            onCommit={(val) => commitPara(i, val)}
            className={paraCls(i)}
            style={paraStyle(i)}
            testId={`editable-body-para-${i}`}
          >
            {highlightPara(p)}
          </Editable>
        ))
      ) : editor ? (
        <Editable
          ctx={ctx}
          as="p"
          value=""
          placeholder="Body copy appears here."
          multiline
          onCommit={(val) => editor.onEditBodyCopy(val)}
          className={`text-[15px] leading-[1.7] ${pullQuote ? "text-center" : ""}`}
          style={{ color: surf.text }}
          testId="editable-body-para-0"
        />
      ) : (
        <p
          className={`text-[15px] leading-[1.7] ${pullQuote ? "text-center" : ""}`}
          style={{ color: surf.text, opacity: 0.6 }}
        >
          Body copy appears here.
        </p>
      );
      const content = (
        <div className={pullQuote ? "px-9 py-12" : "px-7 py-10"}>
          {bodyInner}
          <div className="text-center">
            <BrandCta ctx={ctx} className="mt-6" />
          </div>
        </div>
      );
      return (
        <div
          className="group/blockchrome relative"
          style={{ backgroundColor: surf.bg, color: surf.text, fontFamily: fonts.body }}
          data-testid="preview-body"
        >
          {bodyImageUrl ? (
            <img
              src={bodyImageUrl}
              alt=""
              className="block w-full"
              style={{
                height: EMAIL_BODY_IMAGE_HEIGHT,
                objectFit: "cover",
                objectPosition: "center",
              }}
              data-testid="img-body-copy"
            />
          ) : null}
          {content}
          <BlockEditChrome ctx={ctx} blockKey={key} tone={surf.dark ? "dark" : "light"} />
        </div>
      );
    }
    case "selling_points": {
      const points = instance.sellingPoints || [];
      const heading = punchline || `Why ${ctx.brandName}`;
      // POLISH DIRECTION (task #724): the selling points render as a clean,
      // legible NUMBERED single-column list on the same warm cream surface as the
      // rest of the email. This override is for the polished / preview render
      // only — the shared section-surface map used by the MJML send is untouched.
      const sectionSurf = ctx.style.surfaces.page;
      const commitPoint = (i: number, val: string) =>
        editor?.onPatchInstanceCopy(key, {
          sellingPoints: points.map((p, idx) => (idx === i ? val : p)),
        });
      const movePoint = (i: number, dir: -1 | 1) => {
        const j = i + dir;
        if (j < 0 || j >= points.length) return;
        const next = [...points];
        [next[i], next[j]] = [next[j], next[i]];
        editor?.onPatchInstanceCopy(key, { sellingPoints: next });
      };
      const removePoint = (i: number) =>
        editor?.onPatchInstanceCopy(key, { sellingPoints: points.filter((_, idx) => idx !== i) });
      const addPoint = () => editor?.onPatchInstanceCopy(key, { sellingPoints: [...points, ""] });
      // Supporting image (W5). Only a `ready` image shows (export URL preferred
      // for PNG capture); `pending` shows a skeleton; failed/missing simply omits
      // the image — it's supporting, not critical. Mirrors the hero status
      // contract so preview and sent email never diverge.
      // Image-bearing only when the variant calls for it (icon-grid & type-hero
      // are image-free, and the send path skips generating their photo). The
      // gate MUST match emailVariantNeedsImage so preview/sent agree.
      // Task #2556: apply the main-slot image treatment before the W5 image logic.
      const spTreatment = resolveEmailImageTreatment(instance, EMAIL_IMAGE_TREATMENT_MAIN_SLOT);
      // "selected" overrides the W5 image; "none" suppresses the image; "automatic" is legacy path.
      let spSrc: string | null | undefined = null;
      let spPending = false;
      if (variantNeedsImage && spTreatment.mode === "selected") {
        spSrc = ctx.imageTreatmentExportUrls?.[spTreatment.url] || spTreatment.url;
      } else if (variantNeedsImage && spTreatment.mode !== "none") {
        // automatic: use the W5 pipeline image
        const spReady = ctx.sellingPointsImageStatus?.[key] === "ready";
        spSrc = spReady
          ? ctx.sellingPointsExportUrls?.[key] || ctx.sellingPointsImageUrls?.[key]
          : null;
        spPending = ctx.sellingPointsImageStatus?.[key] === "pending";
      }
      // "none" leaves spSrc null and spPending false → no image rendered.
      const spTypeHero = variant === "type-hero";
      const spCallout = variant === "callout-overlay";
      const spAspect = variant === "checklist-product" || spCallout ? "16 / 9" : "4 / 3";
      const spImage = spSrc ? (
        <img
          src={spSrc}
          alt={heading}
          className="mb-5 w-full rounded-lg"
          style={{ display: "block" }}
          data-testid="preview-selling-points-image"
        />
      ) : spPending ? (
        <div
          data-export-exclude
          className="mb-5 flex w-full items-center justify-center rounded-lg"
          style={{ aspectRatio: spAspect, backgroundColor: tint(colors.accent, 0.1) }}
          data-testid="status-selling-points-generating"
        >
          <span
            className="inline-flex items-center gap-2 text-[11px] font-semibold"
            style={{ color: colors.accent }}
          >
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Generating image…
          </span>
        </div>
      ) : null;
      const spItem = (p: string, i: number) => (
        <li
          key={i}
          className={`group/item flex items-start gap-3 ${spTypeHero ? "text-[18px]" : "text-[15px]"} font-medium leading-snug`}
          style={{ color: colors.text, fontFamily: fonts.body }}
        >
          <span
            className={`flex shrink-0 items-center justify-center rounded-full font-bold ${spTypeHero ? "h-7 w-7 text-[14px]" : "h-6 w-6 text-[13px]"}`}
            style={{ backgroundColor: tint(colors.accent, 0.14), color: colors.accent }}
            aria-hidden
          >
            {i + 1}
          </span>
          {points.length ? (
            <Editable
              ctx={ctx}
              as="span"
              value={p}
              multiline
              onCommit={(val) => commitPoint(i, val)}
              style={{ whiteSpace: "pre-line" }}
              testId={`editable-selling-point-${i}`}
            />
          ) : (
            p
          )}
          {ctx.editor && points.length > 0 && (
            <EmailListItemControls
              keyId={`sp-${i}`}
              onMoveUp={() => movePoint(i, -1)}
              onMoveDown={() => movePoint(i, 1)}
              canMoveUp={i > 0}
              canMoveDown={i < points.length - 1}
              onRemove={() => removePoint(i)}
            />
          )}
        </li>
      );
      const spList = (
        <ul className="space-y-3.5">
          {(points.length ? points : ["Add selling points to populate this block"]).map((p, i) =>
            spItem(p, i),
          )}
        </ul>
      );
      return (
        <div className="group/blockchrome relative px-7 py-10" style={{ backgroundColor: sectionSurf.bg }}>
          {sectionEyebrowNode}
          <SectionHeading
            text={heading}
            ctx={ctx}
            onEdit={(v) => editor?.onEditPunchline(key, v)}
          />
          {spImage}
          {spCallout && spImage ? (
            <div
              className="rounded-xl px-5 py-5 shadow-sm"
              style={{ backgroundColor: colors.card, border: `1px solid ${tint(colors.accent, 0.18)}` }}
            >
              {spList}
            </div>
          ) : (
            spList
          )}
          {ctx.editor && (
            <div contentEditable={false} data-export-exclude className="mt-4 flex justify-center">
              <EmailInlineAddButton label="Add point" onClick={addPoint} testId="button-add-selling-point" />
            </div>
          )}
          <BlockEditChrome ctx={ctx} blockKey={key} tone="light" />
        </div>
      );
    }
    case "reviews": {
      const reviews = instance.reviews || [];
      const removeReview = (i: number) =>
        editor?.onPatchInstanceCopy(key, { reviews: reviews.filter((_, idx) => idx !== i) });
      const addReview = () =>
        editor?.onPatchInstanceCopy(key, { reviews: [...reviews, { quote: "", author: "" }] });
      // No generated image; the three variants restyle the testimonial layout:
      //  • single-hero — first review featured large, the rest stacked beneath.
      //  • two-column  — reviews laid out in a 2-up grid.
      //  • ugc-stack   — equal-weight stacked cards.
      const revTwoCol = variant === "two-column";
      const revSingleHero = variant === "single-hero";
      // POLISH DIRECTION (task #724): reviews sit on the SAME flat warm cream
      // surface as the neighbouring sections — no saturated coral/accent band and
      // no pale-coral card tint. The testimonial cards are flat (transparent over
      // the cream) with only a hairline neutral divider, so the section reads as
      // one continuous parchment. This override is for the polished / preview
      // render only; the shared section-surface map (used by the MJML send) is
      // intentionally left untouched.
      const sectionSurf = ctx.style.surfaces.page;
      const onColorBand = false;
      const reviewCardBg = "transparent";
      const reviewCardBorder = `1px solid ${tint(colors.text, 0.1)}`;
      return (
        <div
          className="group/blockchrome relative px-7 py-10 space-y-3.5"
          style={{ backgroundColor: sectionSurf.bg, fontFamily: fonts.body }}
        >
          <SectionHeading
            text={punchline}
            ctx={ctx}
            onEdit={(v) => editor?.onEditPunchline(key, v)}
            headingColor={onColorBand ? surf.text : undefined}
            ruleColor={onColorBand ? surf.text : undefined}
          />
          <div className={revTwoCol ? "grid grid-cols-2 gap-3.5" : "space-y-3.5"}>
          {(reviews.length ? reviews : [{ quote: "Add reviews to populate this block", author: "" }]).map(
            (r, i) => (
              <div
                key={i}
                className={`group/item relative rounded-[10px] ${revSingleHero && i === 0 ? "px-6 py-6" : "px-5 py-4"}`}
                style={{
                  backgroundColor: reviewCardBg,
                  border: reviewCardBorder,
                }}
              >
                {ctx.editor && reviews.length > 0 && (
                  <EmailCornerRemove keyId={`review-${i}`} onRemove={() => removeReview(i)} />
                )}
                <div className="text-[15px] tracking-[0.15em]" style={{ color: colors.accent }}>
                  ★★★★★
                </div>
                <p className={`mt-3 italic leading-[1.55] ${revSingleHero && i === 0 ? "text-[18px]" : "text-[15px]"}`} style={{ color: colors.text }}>
                  {/* Decorative quotes stay outside the editable span. */}
                  &ldquo;
                  {reviews.length ? (
                    <Editable
                      ctx={ctx}
                      as="span"
                      value={r.quote}
                      multiline
                      onCommit={(val) =>
                        editor?.onPatchInstanceCopy(key, {
                          reviews: reviews.map((rr, idx) =>
                            idx === i ? { ...rr, quote: val } : rr,
                          ),
                        })
                      }
                      style={{ whiteSpace: "pre-line" }}
                      testId={`editable-review-quote-${i}`}
                    />
                  ) : (
                    r.quote
                  )}
                  &rdquo;
                </p>
                {(r.author || (reviews.length && ctx.editor)) && (
                  <p
                    className="mt-4 text-[12px] font-bold uppercase tracking-wide"
                    style={{ color: colors.text }}
                  >
                    —{" "}
                    {reviews.length ? (
                      <Editable
                        ctx={ctx}
                        as="span"
                        value={r.author}
                        placeholder="Author"
                        onCommit={(val) =>
                          editor?.onPatchInstanceCopy(key, {
                            reviews: reviews.map((rr, idx) =>
                              idx === i ? { ...rr, author: val } : rr,
                            ),
                          })
                        }
                        testId={`editable-review-author-${i}`}
                      />
                    ) : (
                      r.author
                    )}
                  </p>
                )}
              </div>
            ),
          )}
          </div>
          {ctx.editor && (
            <div
              contentEditable={false}
              data-export-exclude
              className="flex flex-wrap items-center justify-center gap-2 pt-1"
            >
              <EmailReviewPicker
                available={ctx.editor.availableReviews}
                items={reviews}
                onChange={(items) => editor?.onPatchInstanceCopy(key, { reviews: items })}
              />
              <EmailInlineAddButton label="Add review" onClick={addReview} testId="button-add-review" />
            </div>
          )}
          <BlockEditChrome ctx={ctx} blockKey={key} tone="light" />
        </div>
      );
    }
    case "us_vs_them": {
      const rows = instance.usVsThem || [];
      const narrow = (ctx.designWidth ?? 600) <= 480;
      const cellPad = narrow ? "14px 10px" : "28px 28px";
      const wrap = { wordBreak: "normal", overflowWrap: "break-word" } as const;
      const commitRow = (i: number, patch: { us?: string; them?: string }) =>
        editor?.onPatchInstanceCopy(key, {
          usVsThem: rows.map((rr, idx) => (idx === i ? { ...rr, ...patch } : rr)),
        });
      const removeUvtRow = (i: number) =>
        editor?.onPatchInstanceCopy(key, { usVsThem: rows.filter((_, idx) => idx !== i) });
      const addUvtRow = () =>
        editor?.onPatchInstanceCopy(key, { usVsThem: [...rows, { us: "", them: "" }] });
      return (
        <div className={`group/blockchrome relative ${narrow ? "px-6 py-9" : "px-11 py-10"}`} style={{ backgroundColor: surf.bg }}>
          {sectionEyebrowNode}
          <SectionHeading
            text={punchline}
            ctx={ctx}
            onEdit={(v) => editor?.onEditPunchline(key, v)}
          />
          {/* Image only for split-visual (the other variants are image-free, and
              the send path skips generating their comparison graphic).
              Task #2556: apply the main-slot treatment before the pipeline image. */}
          {(() => {
            if (!variantNeedsImage) return null;
            const uvtTreatment = resolveEmailImageTreatment(instance, EMAIL_IMAGE_TREATMENT_MAIN_SLOT);
            if (uvtTreatment.mode === "selected") {
              const src = ctx.imageTreatmentExportUrls?.[uvtTreatment.url] || uvtTreatment.url;
              return (
                <img
                  src={src}
                  alt={`${ctx.brandName} vs. others`}
                  className="mb-5 w-full rounded-lg"
                  style={{ display: "block" }}
                  data-testid="img-usvsthem"
                />
              );
            }
            if (uvtTreatment.mode === "none") return null;
            // automatic: use the pipeline image
            if (ctx.usVsThemImageStatus === "pending") {
              return (
                <div
                  data-export-exclude
                  className="mb-5 w-full animate-pulse rounded-lg"
                  style={{ aspectRatio: "1 / 1", backgroundColor: tint(colors.text, 0.06) }}
                  data-testid="skeleton-usvsthem-image"
                />
              );
            }
            if (ctx.usVsThemImageUrl) {
              return (
                <img
                  src={ctx.usVsThemExportUrl || ctx.usVsThemImageUrl}
                  alt={`${ctx.brandName} vs. others`}
                  className="mb-5 w-full rounded-lg"
                  style={{ display: "block" }}
                  data-testid="img-usvsthem"
                />
              );
            }
            return null;
          })()}
          {variant === "badge-wall" ? (
            // Positive "us"-forward badge cards; "them" stays an editable muted
            // contrast line so the comparison data round-trips on every variant.
            <div className="space-y-3" style={{ fontFamily: fonts.body }}>
              {(rows.length ? rows : [{ us: "Add comparison rows", them: "—" }]).map((r, i) => (
                <div
                  key={`badge-${i}`}
                  className="group/item relative flex items-start gap-3 rounded-xl px-4 py-3.5"
                  style={{ backgroundColor: tint(colors.accent, 0.06), border: `1px solid ${tint(colors.accent, 0.22)}` }}
                >
                  <span
                    className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[14px] font-bold"
                    style={{ backgroundColor: colors.primary, color: colors.onPrimary }}
                    aria-hidden
                  >
                    ✓
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="text-[15px] font-semibold leading-snug" style={{ color: colors.text }}>
                      {rows.length ? (
                        <Editable
                          ctx={ctx}
                          as="span"
                          value={r.us}
                          multiline
                          onCommit={(val) => commitRow(i, { us: val })}
                          style={{ whiteSpace: "pre-line" }}
                          testId={`editable-usvsthem-us-${i}`}
                        />
                      ) : (
                        r.us
                      )}
                    </div>
                    <div className="mt-1 text-[13px] leading-snug" style={{ color: tint(colors.text, 0.5) }}>
                      Others:{" "}
                      {rows.length ? (
                        <Editable
                          ctx={ctx}
                          as="span"
                          value={r.them}
                          multiline
                          onCommit={(val) => commitRow(i, { them: val })}
                          style={{ whiteSpace: "pre-line" }}
                          testId={`editable-usvsthem-them-${i}`}
                        />
                      ) : (
                        r.them
                      )}
                    </div>
                  </div>
                  {ctx.editor && rows.length > 0 && (
                    <EmailCornerRemove keyId={`uvt-${i}`} onRemove={() => removeUvtRow(i)} />
                  )}
                </div>
              ))}
            </div>
          ) : (
            (() => {
              // table-bold (and split-visual) use a strong brand header + heavier
              // row banding; table-minimal stays quiet with an accent underline.
              const uvtBold = variant === "table-bold" || variant === "split-visual";
              const usHeadStyle: CSSProperties = uvtBold
                ? { padding: cellPad, ...wrap, backgroundColor: colors.primary, color: colors.onPrimary, fontFamily: fonts.heading }
                : { padding: cellPad, ...wrap, color: colors.text, fontFamily: fonts.heading, borderBottom: `2px solid ${colors.accent}` };
              const themHeadStyle: CSSProperties = uvtBold
                ? { padding: cellPad, ...wrap, backgroundColor: tint(colors.text, 0.06), color: tint(colors.text, 0.6), fontFamily: fonts.heading }
                : { padding: cellPad, ...wrap, color: tint(colors.text, 0.55), fontFamily: fonts.heading, borderBottom: `2px solid ${tint(colors.text, 0.15)}` };
              return (
                <div
                  className="grid grid-cols-2 overflow-hidden text-[14px]"
                  style={{ fontFamily: fonts.body }}
                >
                  <div className="font-bold uppercase tracking-wide text-[13px]" style={usHeadStyle}>
                    {ctx.brandName}
                  </div>
                  <div className="font-bold uppercase tracking-wide text-[13px]" style={themHeadStyle}>
                    Others
                  </div>
                  {(rows.length ? rows : [{ us: "Add comparison rows", them: "—" }]).flatMap((r, i) => {
                    const rowBg = uvtBold
                      ? i % 2 === 0
                        ? colors.card
                        : tint(colors.text, 0.05)
                      : i % 2 === 0
                        ? colors.card
                        : tint(colors.text, 0.02);
                    return [
                      <div
                        key={`us-${i}`}
                        className="font-medium leading-snug"
                        style={{ padding: cellPad, ...wrap, backgroundColor: rowBg, color: colors.text, borderLeft: `3px solid ${colors.accent}` }}
                      >
                        {rows.length ? (
                          <Editable
                            ctx={ctx}
                            as="span"
                            value={r.us}
                            multiline
                            onCommit={(val) => commitRow(i, { us: val })}
                            style={{ whiteSpace: "pre-line" }}
                            testId={`editable-usvsthem-us-${i}`}
                          />
                        ) : (
                          r.us
                        )}
                      </div>,
                      <div
                        key={`them-${i}`}
                        className="group/item relative leading-snug"
                        style={{ padding: cellPad, ...wrap, backgroundColor: rowBg, color: tint(colors.text, 0.55) }}
                      >
                        {rows.length ? (
                          <Editable
                            ctx={ctx}
                            as="span"
                            value={r.them}
                            multiline
                            onCommit={(val) => commitRow(i, { them: val })}
                            style={{ whiteSpace: "pre-line" }}
                            testId={`editable-usvsthem-them-${i}`}
                          />
                        ) : (
                          r.them
                        )}
                        {ctx.editor && rows.length > 0 && (
                          <EmailCornerRemove keyId={`uvt-${i}`} onRemove={() => removeUvtRow(i)} />
                        )}
                      </div>,
                    ];
                  })}
                </div>
              );
            })()
          )}
          {ctx.editor && (
            <div contentEditable={false} data-export-exclude className="mt-4 flex justify-center">
              <EmailInlineAddButton label="Add row" onClick={addUvtRow} testId="button-add-uvt-row" />
            </div>
          )}
          <BlockEditChrome ctx={ctx} blockKey={key} tone="light" />
        </div>
      );
    }
    case "comparison_table": {
      const table = instance.comparisonTable;
      const columns = table?.columns?.length ? table.columns : [ctx.brandName, "Others"];
      const tableRows = table?.rows?.length
        ? table.rows
        : [{ feature: "Add comparison rows", values: columns.map(() => "—") }];
      const gridCols = `minmax(0, 1.7fr) repeat(${columns.length}, minmax(0, 1fr))`;
      const narrow = (ctx.designWidth ?? 600) <= 480;
      const featPad = narrow ? "14px 12px" : "20px 24px";
      const valPad = narrow ? "14px 8px" : "20px 16px";
      const wrap = { wordBreak: "normal", overflowWrap: "break-word" } as const;
      // Only the real (persisted) table is editable inline; the placeholder
      // fallback stays read-only (populate it via the panel or Regenerate).
      const tableEditable = !!ctx.editor && !!table?.rows?.length;
      const colsEditable = !!ctx.editor && !!table?.columns?.length;
      const patchTable = (cols: string[], rws: { feature: string; values: string[] }[]) =>
        editor?.onPatchInstanceCopy(key, { comparisonTable: { columns: cols, rows: rws } });
      // Add/remove operate on the REAL persisted data (resolved defaults seed the
      // first add when building from an empty table).
      const realCols = table?.columns?.length ? table.columns : [ctx.brandName, "Others"];
      const realRows = table?.rows ?? [];
      const addColumn = () =>
        patchTable(
          [...realCols, ""],
          realRows.map((r) => ({ feature: r.feature, values: [...(r.values ?? []), ""] })),
        );
      const removeColumn = (ci: number) => {
        if (realCols.length <= 1) return;
        patchTable(
          realCols.filter((_, i) => i !== ci),
          realRows.map((r) => ({
            feature: r.feature,
            values: (r.values ?? []).filter((_, i) => i !== ci),
          })),
        );
      };
      const addComparisonRow = () =>
        patchTable(realCols, [...realRows, { feature: "", values: realCols.map(() => "") }]);
      const removeComparisonRow = (ri: number) =>
        patchTable(realCols, realRows.filter((_, i) => i !== ri));
      // Visual variants (copy/editing identical across all): grid-classic (bold
      // grid), prose-table (quiet underlined left-aligned read), annotated-product
      // (adds the photo), graphic-card (whole table framed in a rounded card).
      const cmpProse = variant === "prose-table";
      const cmpCard = variant === "graphic-card";
      // POLISH DIRECTION (task #724): the comparison table sits on the SAME warm
      // cream surface as its neighbours (override for the polished / preview
      // render only; the shared section-surface map used by the MJML send is
      // untouched). The table itself is additionally pixel-protected
      // (DENSE_TEXT_BLOCK_TYPES) so the AI polish can never crop or garble it.
      const sectionSurf = ctx.style.surfaces.page;
      return (
        <div className={`group/blockchrome relative ${narrow ? "px-6 pt-9 pb-6" : "px-11 pt-10 pb-7"}`} style={{ backgroundColor: sectionSurf.bg }}>
          {sectionEyebrowNode}
          <SectionHeading
            text={punchline}
            ctx={ctx}
            onEdit={(v) => editor?.onEditPunchline(key, v)}
          />
          {/* Image only for annotated-product; the other variants are image-free
              and the send path skips generating their comparison graphic.
              Task #2556: apply the main-slot treatment before the pipeline image. */}
          {(() => {
            if (!variantNeedsImage) return null;
            const cmpTreatment = resolveEmailImageTreatment(instance, EMAIL_IMAGE_TREATMENT_MAIN_SLOT);
            if (cmpTreatment.mode === "selected") {
              const src = ctx.imageTreatmentExportUrls?.[cmpTreatment.url] || cmpTreatment.url;
              return (
                <img
                  src={src}
                  alt={`${ctx.brandName} comparison`}
                  className="mb-5 w-full rounded-lg"
                  style={{ display: "block" }}
                  data-testid="img-comparison-table"
                />
              );
            }
            if (cmpTreatment.mode === "none") return null;
            // automatic: use the pipeline image
            if (ctx.comparisonTableImageStatus === "pending") {
              return (
                <div
                  data-export-exclude
                  className="mb-5 w-full animate-pulse rounded-lg"
                  style={{ aspectRatio: "1 / 1", backgroundColor: tint(colors.text, 0.06) }}
                  data-testid="skeleton-comparison-image"
                />
              );
            }
            if (ctx.comparisonTableImageUrl) {
              return (
                <img
                  src={ctx.comparisonTableExportUrl || ctx.comparisonTableImageUrl}
                  alt={`${ctx.brandName} comparison`}
                  className="mb-5 w-full rounded-lg"
                  style={{ display: "block" }}
                  data-testid="img-comparison-table"
                />
              );
            }
            return null;
          })()}
          {/* prose-table drops the filled header band for a quiet underlined,
              left-aligned reading layout; graphic-card frames the whole table in
              a rounded card; grid-classic / annotated-product keep the bold grid. */}
          <div
            className={`overflow-hidden text-[14px] ${cmpCard ? "rounded-xl shadow-sm" : ""}`}
            style={{ fontFamily: fonts.body, ...(cmpCard ? { border: `1px solid ${tint(colors.text, 0.12)}` } : {}) }}
          >
            <div className="grid" style={{ gridTemplateColumns: gridCols }}>
              <div style={{ padding: featPad, backgroundColor: cmpProse ? "transparent" : tint(colors.text, 0.04), borderBottom: cmpProse ? `2px solid ${tint(colors.text, 0.12)}` : undefined }} />
              {columns.map((c, ci) => (
                <div
                  key={ci}
                  className={`group/item relative font-bold uppercase tracking-wide text-[13px] ${cmpProse ? "text-left" : "text-center"}`}
                  style={{
                    padding: valPad,
                    ...wrap,
                    backgroundColor: cmpProse
                      ? "transparent"
                      : ci === 0
                        ? colors.primary
                        : tint(colors.text, 0.06),
                    color: cmpProse
                      ? ci === 0
                        ? colors.text
                        : tint(colors.text, 0.6)
                      : ci === 0
                        ? colors.onPrimary
                        : tint(colors.text, 0.6),
                    fontFamily: fonts.heading,
                    borderBottom: cmpProse ? `2px solid ${ci === 0 ? colors.accent : tint(colors.text, 0.12)}` : undefined,
                  }}
                >
                  {colsEditable ? (
                    <Editable
                      ctx={ctx}
                      as="span"
                      value={c}
                      onCommit={(val) =>
                        patchTable(
                          columns.map((cc, idx) => (idx === ci ? val : cc)),
                          tableRows,
                        )
                      }
                      testId={`editable-comparison-col-${ci}`}
                    />
                  ) : (
                    c
                  )}
                  {colsEditable && columns.length > 1 && (
                    <EmailCornerRemove keyId={`col-${ci}`} onRemove={() => removeColumn(ci)} />
                  )}
                </div>
              ))}
            </div>
            {tableRows.map((r, ri) => {
              const rowBg = ri % 2 === 0 ? colors.card : tint(colors.text, 0.03);
              return (
                <div key={ri} className="grid" style={{ gridTemplateColumns: gridCols }}>
                  <div
                    className="group/item relative font-semibold"
                    style={{ padding: featPad, ...wrap, backgroundColor: rowBg, color: colors.text }}
                  >
                    {tableEditable ? (
                      <Editable
                        ctx={ctx}
                        as="span"
                        value={r.feature}
                        multiline
                        onCommit={(val) =>
                          patchTable(
                            columns,
                            tableRows.map((rr, idx) =>
                              idx === ri ? { ...rr, feature: val } : rr,
                            ),
                          )
                        }
                        style={{ whiteSpace: "pre-line" }}
                        testId={`editable-comparison-feature-${ri}`}
                      />
                    ) : (
                      r.feature
                    )}
                    {tableEditable && (
                      <EmailCornerRemove
                        keyId={`comparison-row-${ri}`}
                        onRemove={() => removeComparisonRow(ri)}
                      />
                    )}
                  </div>
                  {columns.map((_, ci) => {
                    // A row's value can be a checkmark token (✓/✗/yes/no) OR a
                    // prose phrase. Prose cells left-align and gain line-height so
                    // they read; checkmark tokens stay centered. The prose-table
                    // variant left-aligns everything regardless.
                    const cellVal = r.values?.[ci] ?? "";
                    const cellIsCheck = emailComparisonValueIsCheckmark(cellVal);
                    const cellAlign = cmpProse || !cellIsCheck ? "text-left" : "text-center";
                    return (
                    <div
                      key={ci}
                      className={cellAlign}
                      style={{
                        padding: valPad,
                        ...wrap,
                        lineHeight: cellIsCheck ? undefined : 1.45,
                        backgroundColor: cmpProse ? "transparent" : ci === 0 ? tint(colors.accent, 0.06) : rowBg,
                        color: ci === 0 ? colors.text : tint(colors.text, 0.6),
                        fontWeight: ci === 0 ? 600 : 400,
                      }}
                    >
                      {tableEditable ? (
                        <Editable
                          ctx={ctx}
                          as="span"
                          value={r.values?.[ci] ?? ""}
                          placeholder="—"
                          onCommit={(val) =>
                            patchTable(
                              columns,
                              tableRows.map((rr, idx) => {
                                if (idx !== ri) return rr;
                                const values = columns.map(
                                  (__, vi) => rr.values?.[vi] ?? "",
                                );
                                values[ci] = val;
                                return { ...rr, values };
                              }),
                            )
                          }
                          testId={`editable-comparison-value-${ri}-${ci}`}
                        />
                      ) : (
                        r.values?.[ci] ?? "—"
                      )}
                    </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
          {ctx.editor && (
            <div
              contentEditable={false}
              data-export-exclude
              className="mt-4 flex items-center justify-center gap-2"
            >
              <EmailInlineAddButton
                label="Add row"
                onClick={addComparisonRow}
                testId="button-add-comparison-row"
              />
              <EmailInlineAddButton
                label="Add column"
                onClick={addColumn}
                testId="button-add-comparison-column"
              />
            </div>
          )}
          <BlockEditChrome ctx={ctx} blockKey={key} tone="light" />
        </div>
      );
    }
    case "did_you_know": {
      // Supporting image (W8). Only a `ready` image shows (export URL preferred
      // for PNG capture); `pending` shows a skeleton; failed/missing simply omits
      // the image — it's supporting, not critical. Mirrors the selling-points
      // status contract so preview and sent email never diverge.
      // Image only for image-editorial & split-contrast; color-band and
      // illustrated-callout are image-free (send path skips their photo too).
      // Task #2556: apply the main-slot image treatment.
      const dykTreatment = resolveEmailImageTreatment(instance, EMAIL_IMAGE_TREATMENT_MAIN_SLOT);
      let dykSrc: string | null | undefined = null;
      let dykPending = false;
      if (variantNeedsImage && dykTreatment.mode === "selected") {
        dykSrc = ctx.imageTreatmentExportUrls?.[dykTreatment.url] || dykTreatment.url;
      } else if (variantNeedsImage && dykTreatment.mode !== "none") {
        // automatic: use the W8 pipeline image
        const dykReady = ctx.didYouKnowImageStatus?.[key] === "ready";
        dykSrc = dykReady
          ? ctx.didYouKnowExportUrls?.[key] || ctx.didYouKnowImageUrls?.[key]
          : null;
        dykPending = ctx.didYouKnowImageStatus?.[key] === "pending";
      }
      // "none" leaves dykSrc null and dykPending false → no image rendered.
      const dykContrast = variant === "split-contrast" || variant === "color-band";
      const dykIllustrated = variant === "illustrated-callout";
      const darkS = ctx.style.surfaces.dark;
      const calloutBg = dykContrast ? darkS.bg : tint(colors.accent, 0.08);
      const calloutLabel = dykContrast ? darkS.text : colors.accent;
      const calloutText = dykContrast ? darkS.text : colors.text;
      // Satori-baked feature tile: the MJML embeds it (~line 521) ONLY when there
      // is no W8 photo and the variant is image-bearing — it replaces the live
      // callout entirely. Mirror that precedence here.
      // Task #2556: "none" suppresses the raster tile too, matching email-mjml.ts —
      // otherwise clearing the DYK image would still show the baked feature tile.
      const tilePng =
        variantNeedsImage && dykTreatment.mode !== "none" && !dykSrc && !dykPending
          ? ctx.rasterizedBlocks?.[key] || null
          : null;
      return (
        <div className="group/blockchrome relative px-7 py-10" style={{ backgroundColor: surf.bg }}>
          {sectionEyebrowNode}
          {dykSrc ? (
            <img
              src={dykSrc}
              alt="Did you know?"
              className="mb-5 w-full rounded-lg"
              style={{ display: "block" }}
              data-testid="preview-did-you-know-image"
            />
          ) : dykPending ? (
            <div
              data-export-exclude
              className="mb-5 flex w-full items-center justify-center rounded-lg"
              style={{ aspectRatio: "4 / 3", backgroundColor: tint(colors.accent, 0.1) }}
              data-testid="status-did-you-know-generating"
            >
              <span
                className="inline-flex items-center gap-2 text-[11px] font-semibold"
                style={{ color: colors.accent }}
              >
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Generating image…
              </span>
            </div>
          ) : tilePng ? (
            <img
              src={tilePng}
              alt="Did you know?"
              className="w-full rounded-lg"
              style={{ display: "block" }}
              data-testid="preview-did-you-know-tile"
            />
          ) : null}
          {!tilePng && (
          <div
            className={`rounded-lg px-5 py-4 ${dykIllustrated ? "flex items-start gap-4" : ""}`}
            style={{
              backgroundColor: calloutBg,
              borderLeft: dykContrast ? undefined : `4px solid ${colors.accent}`,
              fontFamily: fonts.body,
              ...(dykIllustrated ? { borderLeft: undefined } : {}),
            }}
          >
            {dykIllustrated && (
              <span
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-[20px] font-extrabold"
                style={{ backgroundColor: colors.accent, color: colors.onPrimary, fontFamily: fonts.heading }}
                aria-hidden
              >
                ?
              </span>
            )}
            <div className="min-w-0 flex-1">
              <div
                className="mb-2 font-bold uppercase tracking-[0.12em]"
                style={{ color: calloutLabel, fontFamily: fonts.heading, fontSize: `${EMAIL_TYPE_SCALE.eyebrow}px` }}
              >
                Did you know?
              </div>
              <Editable
                ctx={ctx}
                as="p"
                value={instance.didYouKnow || ""}
                placeholder="Add a did-you-know fact to populate this block."
                multiline
                onCommit={(v) => editor?.onPatchInstanceCopy(key, { didYouKnow: v })}
                className="leading-[1.6]"
                style={{ color: calloutText, whiteSpace: "pre-line", fontSize: `${EMAIL_TYPE_SCALE.bodyLarge}px` }}
                testId="editable-did-you-know"
              />
            </div>
          </div>
          )}
          <BlockEditChrome ctx={ctx} blockKey={key} tone="light" />
        </div>
      );
    }
    case "product_list": {
      // Image source by type: a "bundle" block shows the bundle's member photos;
      // a "range" block shows whatever product photos are already available.
      // Legacy blocks have no stored type but were created bundle-only, so infer
      // "bundle" when member photos exist; otherwise default to "range".
      const bundleImages = ctx.productListImages ?? [];
      const rangeImages = (ctx.productImageUrls ?? []).map((url) => ({
        name: ctx.brandName,
        imageUrl: url,
      }));
      const plType: ProductListType =
        instance.productListType ?? (bundleImages.length ? "bundle" : "range");
      const images = plType === "bundle" ? bundleImages : rangeImages;
      if (!images.length) return null;
      // Same-origin data-URL copies (index-aligned with the chosen source) are
      // swapped in during PNG export so cross-origin images don't taint it.
      const exportUrls =
        plType === "bundle" ? ctx.productExportUrls : ctx.productImageExportUrls;
      // Prefer a stored catalog variant ("horizontal-feature" maps onto the
      // existing horizontal-scroll layout); otherwise fall back to the historical
      // type+count default.
      const storedPlVariant = instance.layoutVariant;
      const plCatalog = emailLayoutVariantsFor("product_list");
      const variant: ProductListVariant =
        storedPlVariant && plCatalog.includes(storedPlVariant)
          ? storedPlVariant === "horizontal-feature"
            ? "horizontal-scroll-style"
            : (storedPlVariant as ProductListVariant)
          : resolveProductListVariant(plType, images.length);
      // Task #2556: resolve per-tile image treatment. Slot is tile_N (0-based
      // positional index into the visible tile array). A "selected" treatment
      // replaces the tile image; "none" removes the tile from the grid; "automatic"
      // preserves existing behavior. Tiles removed by "none" are excluded before
      // the grid layout is derived so counts/positions are consistent.
      const resolveTileImageSrc = (i: number): string | null => {
        const slot = productListTileSlot(i);
        const t = resolveEmailProductListImageTreatment(instance, slot);
        if (t.mode === "selected") {
          return ctx.imageTreatmentExportUrls?.[t.url] || t.url;
        }
        // automatic: use the original pipeline image
        return exportUrls?.[i] || images[i].imageUrl;
      };
      // Build the effective tile list: each entry has the resolved src and name.
      const effectiveTiles = images
        .map((m, i) => {
          const src = resolveTileImageSrc(i);
          if (!src) return null;
          return { src, name: m.name, origIndex: i };
        })
        .filter(Boolean) as { src: string; name: string; origIndex: number }[];
      const tile = (ei: number, extraStyle?: CSSProperties) => {
        const t = effectiveTiles[ei];
        return (
          <img
            key={ei}
            src={t.src}
            alt={t.name}
            className="w-full rounded-[10px] object-cover"
            style={{
              aspectRatio: "1 / 1",
              border: `1px solid ${tint(colors.text, 0.08)}`,
              ...extraStyle,
            }}
            data-testid={`img-product-${t.origIndex}`}
          />
        );
      };
      // No effective tiles left (all suppressed via "none") → render nothing.
      if (!effectiveTiles.length) return null;
      let grid: ReactNode;
      if (variant === "editorial-feature" && effectiveTiles.length > 1) {
        // Hero product large with its companions stacked beside it.
        grid = (
          <div className="grid gap-3" style={{ gridTemplateColumns: "1.6fr 1fr" }}>
            {tile(0, { aspectRatio: "4 / 5" })}
            <div className="grid gap-3" style={{ alignContent: "start" }}>
              {effectiveTiles.slice(1).map((_, i) => tile(i + 1))}
            </div>
          </div>
        );
      } else {
        // grid-2col → 2 per row; grid-3col / horizontal-scroll-style (NO scroll
        // in email — mirrors the MJML send renderer) / single-image editorial
        // fallback → up to 3 per row. Products wrap to new rows; the section is
        // never horizontally scrollable.
        const maxCols =
          variant === "grid-2col" || variant === "editorial-feature" ? 2 : 3;
        const cols = Math.min(effectiveTiles.length, maxCols);
        grid = (
          <div
            className="grid gap-3"
            style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}
          >
            {effectiveTiles.map((_, i) => tile(i))}
          </div>
        );
      }
      return (
        <div className="group/blockchrome relative px-7 py-10" style={{ backgroundColor: surf.bg }}>
          <BlockEditChrome ctx={ctx} blockKey={key} tone="light" />
          {sectionEyebrowNode}
          <SectionHeading
            text={punchline}
            ctx={ctx}
            onEdit={(v) => editor?.onEditPunchline(key, v)}
          />
          <Editable
            ctx={ctx}
            as="p"
            value={instance.productListIntro || ""}
            placeholder="Add a product-list intro to populate this block."
            multiline
            onCommit={(v) => editor?.onPatchInstanceCopy(key, { productListIntro: v })}
            className="text-[15px] leading-[1.6] mb-5 text-center"
            style={{ color: colors.text, fontFamily: fonts.body, whiteSpace: "pre-line" }}
            testId="editable-product-list-intro"
          />
          {grid}
        </div>
      );
    }
    case "image_only": {
      // Full-width product photo — a pure visual break (V2 parity). Uses REAL
      // product photos (never AI): the export data-URL when capturing, else the
      // live product image. Nothing on file → the block renders nothing.
      // A per-instance selection (chosen product photo or custom upload,
      // Task #2286) wins over the default first product photo.
      const chosen = instance.imageUrl || "";
      const imgSrc = chosen
        ? ctx.blockImageExportUrls?.[chosen] || chosen
        : ctx.productImageExportUrls?.[0] ||
          ctx.productExportUrls?.[0] ||
          ctx.productImageUrls?.[0] ||
          "";
      if (!imgSrc && !editor) return null;
      return (
        <div
          className="group/blockchrome relative px-0"
          style={{ backgroundColor: surf.bg }}
          data-testid="preview-image-only"
        >
          <ImageBlockChrome ctx={ctx} blockKey={key} hasSelection={!!chosen} />
          {imgSrc ? (
            <img
              src={imgSrc}
              alt={ctx.brandName}
              className="block w-full"
              style={{ maxHeight: 480, objectFit: "cover" }}
              data-testid="img-image-only"
            />
          ) : (
            // Editor-only placeholder (never captured/sent — imgSrc-less blocks
            // return null off the editor): gives the picker chrome a surface
            // when the product has no photos yet.
            <div
              className="flex h-32 items-center justify-center text-muted-foreground"
              style={{ fontSize: `${EMAIL_TYPE_SCALE.caption}px` }}
              data-export-exclude
            >
              Choose or upload an image for this block
            </div>
          )}
        </div>
      );
    }
    case "image_discount": {
      // Product photo + discount band (V2 parity): a short note and the code in
      // a dashed pill on the brand-primary band. Falls back to the email's sale
      // settings when the instance carries no copy. The selected image
      // (Task #2286) wins over the default first product photo.
      const chosenDisc = instance.imageUrl || "";
      const imgSrc = chosenDisc
        ? ctx.blockImageExportUrls?.[chosenDisc] || chosenDisc
        : ctx.productImageExportUrls?.[0] ||
          ctx.productExportUrls?.[0] ||
          ctx.productImageUrls?.[0] ||
          "";
      const note =
        instance.discountNote ??
        (ctx.sale && ctx.sale !== "No sale" ? ctx.sale : "A little something for you");
      const code = instance.discountCode ?? "";
      return (
        <div
          className="group/blockchrome relative"
          style={{ backgroundColor: surf.bg }}
          data-testid="preview-image-discount"
        >
          <ImageBlockChrome ctx={ctx} blockKey={key} hasSelection={!!chosenDisc} />
          {imgSrc ? (
            <img
              src={imgSrc}
              alt={ctx.brandName}
              className="block w-full"
              style={{ maxHeight: 380, objectFit: "cover" }}
              data-testid="img-image-discount"
            />
          ) : null}
          <div
            className="px-7 py-8 text-center"
            style={{
              backgroundColor: colors.primary,
              color: colors.onPrimary,
              fontFamily: fonts.body,
            }}
          >
            <Editable
              ctx={ctx}
              as="div"
              value={note}
              placeholder="A little something for you"
              onCommit={(v) => editor?.onPatchInstanceCopy(key, { discountNote: v })}
              className="text-[16px] font-semibold leading-snug"
              style={{ fontFamily: fonts.heading }}
              testId="editable-discount-note"
            />
            {(code || ctx.editor) && (
              <div
                className="mt-4 inline-block rounded-md px-5 py-2.5 text-[15px] font-bold tracking-[0.18em] uppercase"
                style={{
                  border: `2px dashed ${colors.onPrimary}`,
                  color: colors.onPrimary,
                }}
                data-testid="pill-discount-code"
              >
                <Editable
                  ctx={ctx}
                  as="span"
                  value={code}
                  placeholder="CODE"
                  onCommit={(v) =>
                    editor?.onPatchInstanceCopy(key, { discountCode: v })
                  }
                  testId="editable-discount-code"
                />
              </div>
            )}
          </div>
        </div>
      );
    }
    case "cta": {
      // Standalone CTA band (V2 parity): short headline + supporting line +
      // the brand CTA button. Empty instance copy falls back to the email-level
      // headline/CTA so a late-added block never renders blank.
      const ctaHeadline = instance.ctaHeadline ?? "Ready when you are";
      const ctaBody = instance.ctaBody ?? "";
      return (
        <div
          className="px-7 py-10 text-center"
          style={{ backgroundColor: surf.bg, color: surf.text, fontFamily: fonts.body }}
          data-testid="preview-cta-block"
        >
          <Editable
            ctx={ctx}
            as="div"
            value={ctaHeadline}
            placeholder="Ready when you are"
            onCommit={(v) => editor?.onPatchInstanceCopy(key, { ctaHeadline: v })}
            className="font-extrabold leading-tight tracking-tight"
            style={{ fontSize: `${EMAIL_TYPE_SCALE.sectionHeading}px`, fontFamily: fonts.heading, color: surf.text }}
            testId="editable-cta-headline"
          />
          {(ctaBody || ctx.editor) && (
            <Editable
              ctx={ctx}
              as="div"
              value={ctaBody}
              placeholder="One click away."
              onCommit={(v) => editor?.onPatchInstanceCopy(key, { ctaBody: v })}
              className="mt-2 leading-relaxed"
              style={{ color: surf.text, opacity: 0.8, fontSize: `${EMAIL_TYPE_SCALE.body}px` }}
              testId="editable-cta-body"
            />
          )}
          <div className="mx-auto mt-5 max-w-[320px]">
            <BrandCta ctx={ctx} className="mt-0" />
          </div>
        </div>
      );
    }
    case "footer":
      return <FooterBlock ctx={ctx} />;
    default:
      return null;
  }
}

// Footer renders from the snapshotted Universal footer asset (merged with the
// per-email `footerOverrides` upstream in the ctx) when present, and degrades
// gracefully (logo/wordmark + default link labels) when it isn't.
//
// When `ctx.editor.footer` is present (editor only — never on the send / export
// paths) the footer elements become editable inline: social links (label, URL,
// delete), the disclaimer/legal line (text, delete), the Preferences/Contact nav
// links (label, URL, delete), and the unsubscribe URL (URL only — NEVER
// deletable, blank falls back to the platform unsubscribe).
// Each edit persists a per-email footer override; the editing chrome carries
// `data-export-exclude` so it can never leak into the sent email or its capture.

// A deletable footer nav link (Preferences / Contact). In editor mode it exposes
// inline label editing, a URL-edit popover (sibling of the anchor, never nested),
// and a delete button — mirroring the social rows. In read mode it renders a real
// anchor, or a plain span when it has no URL. Renders nothing when `deleted` (the
// merged footer carries the per-email deletion flag, hiding it on both surfaces).
function FooterNavLink({
  editing,
  deleted,
  label,
  url,
  defaultLabel,
  keyId,
  platformFallback = false,
  onSetLabel,
  onSetUrl,
  onDelete,
}: {
  editing: boolean;
  deleted: boolean;
  label: string;
  url: string;
  defaultLabel: string;
  keyId: string;
  // When true the link has a platform merge-tag fallback (e.g. Preferences →
  // {% preferences_page %}), so it ALWAYS renders as an anchor even with a blank
  // URL — matching the sent MJML. Contact has no fallback (false), so a blank URL
  // renders as plain text on both surfaces.
  platformFallback?: boolean;
  onSetLabel: (v: string) => void;
  onSetUrl: (v: string) => void;
  onDelete: () => void;
}) {
  if (deleted) return null;
  const display = label.trim() || defaultLabel;
  const hasUrl = !!url.trim();
  const showAsLink = hasUrl || platformFallback;
  // Sanitize before it becomes a live href; blank/unsafe collapses to "#".
  const safeHref = safeFooterHref(url);
  if (!editing) {
    return showAsLink ? (
      <a
        href={safeHref}
        style={{ color: "inherit", textDecoration: "underline" }}
        data-testid={`link-footer-${keyId}`}
      >
        {display}
      </a>
    ) : (
      <span data-testid={`text-footer-${keyId}`}>{display}</span>
    );
  }
  return (
    <span className="group/item inline-flex items-center" data-testid={`footer-${keyId}`}>
      {/* The editable label is a BARE contentEditable, never nested inside an
          anchor — a contentEditable inside <a> is a focus/drag trap that blocks
          click-to-edit. The sibling URL popover owns the link target; the
          underline here just mirrors the read-mode anchor. */}
      <EmailEditableText
        value={label}
        onCommit={onSetLabel}
        placeholder={defaultLabel}
        testId={`text-footer-${keyId}`}
        style={{ color: "inherit", textDecoration: showAsLink ? "underline" : "none" }}
      />
      <EmailLinkUrlPopover keyId={keyId} url={url} onSave={onSetUrl} label={display} />
      <button
        type="button"
        contentEditable={false}
        data-export-exclude
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onDelete();
        }}
        title={`Remove ${defaultLabel}`}
        data-testid={`button-remove-footer-${keyId}`}
        className="ml-0.5 flex h-5 w-5 items-center justify-center rounded text-neutral-300 opacity-0 transition-opacity hover:bg-white/10 hover:text-white group-hover/item:opacity-100"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </span>
  );
}

// The ONE standard footer (user decision, Aug 2026): every email closes with
// the same simple brand-colored footer — brand name, physical address,
// unsubscribe link. Nothing else (no social/preferences/contact/legal) "for
// now". Mirrors standardFooterMjml on the server so previews match sends.
function FooterBlock({ ctx }: { ctx: PreviewCtx }) {
  const { colors, fonts, surfaces } = ctx.style;
  const f = ctx.footer;
  // Footer blends into the email: page background + contrast-safe on-background
  // text (mirrors the server's standardFooterMjml swap). Under a polished /
  // captured preview stack the editor passes `sampledFooter` (bottom-edge color
  // of the last block image + luminance-derived ink) so the preview matches
  // the sent stacked-image email; sampling failure falls back to the brand
  // style's page surface, same order as the send path.
  const footerBg = ctx.sampledFooter?.bg ?? surfaces.page.bg;
  const onFooter = ctx.sampledFooter?.text ?? surfaces.page.text;
  const ed = ctx.editor?.footer;
  const editing = !!ed;

  const unsubLabel = f?.unsubscribeLabel?.trim() || "Unsubscribe";
  const brandLine = f?.companyName?.trim() || ctx.brandName || "";

  return (
    <div
      className="text-center px-6 py-8"
      style={{ backgroundColor: footerBg, color: onFooter, fontFamily: fonts.body }}
    >
      <div
        className="mx-auto mb-4 h-[2px] w-10 rounded-full"
        style={{ backgroundColor: colors.accent }}
      />
      {brandLine && (
        <div className="font-bold tracking-[0.15em] uppercase" style={{ opacity: 0.95, fontSize: EMAIL_TYPE_SCALE.label }}>
          {brandLine}
        </div>
      )}
      {f?.address?.trim() && (
        <div className="mt-1.5 text-[11px] leading-relaxed whitespace-pre-line" style={{ opacity: 0.55 }}>
          {f.address}
        </div>
      )}
      <div className="mt-3.5 text-[11px]" style={{ opacity: 0.7 }}>
        <span className="group/item inline-flex items-center" data-testid="footer-unsubscribe">
          {/* Real anchor in BOTH modes (inert while editing); a blank URL
              renders an inert href because the live value is Klaviyo's
              {{ unsubscribe_link }} merge variable, injected only at send
              time — the preview never shows raw merge-tag text. */}
          <a
            href={safeFooterHref(f?.unsubscribeUrl)}
            onClick={editing ? (e) => e.preventDefault() : undefined}
            style={{ color: "inherit", textDecoration: "underline" }}
            data-testid="link-footer-unsubscribe"
          >
            {unsubLabel}
          </a>
          {editing && (
            <EmailLinkUrlPopover
              keyId="unsubscribe"
              url={f?.unsubscribeUrl ?? ""}
              onSave={(url) => ed?.onSetUnsubscribeUrl(url)}
              label="Unsubscribe"
            />
          )}
        </span>
      </div>
    </div>
  );
}

// =============================================================================
// Headless render → capture → upload → send (used by the list page)
// -----------------------------------------------------------------------------
// Mirrors the editor's Send flow exactly, but mounts the renderer in an
// off-screen React root so a send can happen without ever opening the editor.
// =============================================================================

// Ensure the brand heading/body web fonts are loaded so the off-screen capture
// matches the editor (which injects the same stylesheet on mount).
function ensureEmailFontsLoaded() {
  const id = "email-express-brand-fonts";
  if (document.getElementById(id)) return;
  const link = document.createElement("link");
  link.id = id;
  link.rel = "stylesheet";
  link.href = EMAIL_FONT_LINK_HREF;
  document.head.appendChild(link);
}

// Upload a captured block PNG to durable storage (R2) and return its public
// URL. Reuses the shared /api/upload endpoint, which mirrors to R2.
export async function uploadBlockImage(blob: Blob, filename: string): Promise<string> {
  const form = new FormData();
  form.append("file", blob, filename);
  const res = await fetch("/api/upload", {
    method: "POST",
    body: form,
    credentials: "include",
  });
  if (!res.ok) {
    let msg = `Image upload failed (HTTP ${res.status})`;
    try {
      const j = await res.json();
      if (j?.message) msg = j.message;
    } catch {}
    throw new Error(msg);
  }
  const data = await res.json();
  if (!data?.url) throw new Error("Image upload returned no URL");
  return data.url as string;
}

const nextFrame = () =>
  new Promise<void>((r) => requestAnimationFrame(() => requestAnimationFrame(() => r())));

export type KlaviyoSendResult = EmailDetail & {
  klaviyo?: {
    mode: "campaign" | "template" | "flow" | "flow_template" | "pending" | "done";
    message: string;
  };
};

// The fixed design width every capture renders at — the SAME width the editor's
// desktop preview uses (600px), so a captured section is pixel-identical to
// what the user saw. The sent email embeds these at 600px display width
// (retina 2x via CAPTURE_OPTS.pixelRatio).
export const EMAIL_CAPTURE_WIDTH = 600;

// Does the email still have work in flight that a capture must wait for? Only
// counts assets a VISIBLE block would actually render — a hidden block's
// pending image can never leak into the capture, so it never blocks a send.
export function emailHasPendingAssets(email: EmailDetail): boolean {
  const visible = visibleBlocksOf(email);
  const visibleSet = new Set(visible);
  const visibleTypes = new Set(visible.map(emailBlockType));
  if (email.copyStatus === "pending") return true;
  for (const [key, status] of Object.entries(email.blockCopyStatus ?? {})) {
    if (status === "pending" && visibleSet.has(key)) return true;
  }
  // Preset-template AI image slots: a visible section with a pending slot
  // blocks capture/send exactly like the archetype supporting images.
  // Block-driven template emails use the STANDARD image slots (hero /
  // selling-points / etc. below), so they must NOT take this early return.
  if (email.presetTemplateKey && !isEmailBlockDrivenPresetEmail(email)) {
    for (const img of email.presetImages ?? []) {
      if (img.status === "pending" && visibleSet.has(img.instanceId)) return true;
    }
    return false;
  }
  if (email.heroImageStatus === "pending") return true;
  if (email.usVsThemImageStatus === "pending" && visibleTypes.has("us_vs_them")) {
    return true;
  }
  if (
    email.comparisonTableImageStatus === "pending" &&
    visibleTypes.has("comparison_table")
  ) {
    return true;
  }
  for (const img of email.sellingPointsImages ?? []) {
    if (img.status === "pending" && visibleSet.has(img.instanceId)) return true;
  }
  for (const img of email.didYouKnowImages ?? []) {
    if (img.status === "pending" && visibleSet.has(img.instanceId)) return true;
  }
  return false;
}

// Poll the email until every visible block's copy + supporting images have
// settled (ready or failed — a 202/pending slot must NEVER be captured), then
// return the freshest detail. Throws after `timeoutMs` so a stuck generation
// surfaces as a clear send error instead of hanging forever.
export async function waitForEmailAssetsSettled(
  brandId: string,
  emailId: string,
  opts?: { timeoutMs?: number; intervalMs?: number },
): Promise<EmailDetail> {
  const timeoutMs = opts?.timeoutMs ?? 4 * 60 * 1000;
  const intervalMs = opts?.intervalMs ?? 3500;
  const { apiRequest } = await import("@/lib/queryClient");
  const deadline = Date.now() + timeoutMs;
  for (;;) {
    const res = await apiRequest(
      "GET",
      `/api/brands/${brandId}/email-express/emails/${emailId}`,
    );
    const detail = (await res.json()) as EmailDetail;
    if (!emailHasPendingAssets(detail)) return detail;
    if (Date.now() >= deadline) {
      throw new Error(
        "This email's images are still generating — wait for them to finish, then send again.",
      );
    }
    await new Promise((r) => setTimeout(r, intervalMs));
  }
}

// Unique per-instance selected image URLs (image_only / image_discount blocks,
// Task #2286) across an email's blocks — the source URLs a capture must embed
// as same-origin data URLs so the chosen/uploaded image renders in the PNG.
export function collectEmailBlockImageUrls(
  blocks: string[],
  blockCopy: EmailBlockCopy | null | undefined,
): string[] {
  const urls = new Set<string>();
  for (const key of blocks) {
    const type = emailBlockType(key);
    const inst = resolveEmailInstanceCopy(blockCopy ?? undefined, key);
    if (type === "image_only" || type === "image_discount") {
      if (inst.imageUrl) urls.add(inst.imageUrl);
    } else if (type === "body") {
      const bodyImage = resolveEmailBodyImagePreference(inst);
      if (bodyImage.mode === "selected") urls.add(bodyImage.url);
    }
    // Task #2556: collect "selected" treatment URLs for eligible blocks.
    if (isEmailImageTreatmentEligible(type)) {
      for (const u of collectEmailImageTreatmentUrls(inst, type)) {
        urls.add(u);
      }
    }
  }
  return [...urls];
}

// Build the COMPLETE PreviewCtx for a headless capture — the exact same inputs
// the editor's on-screen preview renders with (hero photo, us-vs-them /
// comparison-table visuals, product photos, selling-points / did-you-know
// images, and the Satori-baked hero band + feature tiles), each cross-origin
// image embedded as a same-origin data URL so nothing renders blank in the
// captured PNG. This is what makes a headless send pixel-identical to the
// preview the user approved.
async function buildEmailCaptureCtx(
  brandId: string,
  email: EmailDetail,
  brand: any,
): Promise<PreviewCtx> {
  // Apply the persisted Brand Styling layer so a headless capture matches both
  // the on-screen preview and the sent MJML. Null/absent → byte-identical.
  const style = resolveEmailBrandStyle(
    brand ?? {},
    (email as any)?.brandStyleDirection ?? null,
  );

  ensureEmailFontsLoaded();

  // Brand-level footer settings (Task #1899): fetched live so the captured
  // footer shows the same physical address + unsubscribe link as the sent MJML.
  // Best-effort — a fetch failure degrades to the snapshot/override values.
  let brandFooterSettings: { physicalAddress?: string; unsubscribeUrl?: string } | null =
    null;
  try {
    const res = await fetch(`/api/brands/${brandId}/email-express/settings`, {
      credentials: "include",
    });
    if (res.ok) brandFooterSettings = await res.json();
  } catch {
    // Non-fatal.
  }

  // Embed the brand logo as a data URL so it renders in the capture
  // (cross-origin/R2 logos would otherwise be blank).
  let logoExportUrl: string | null = null;
  if (style.logoUrl) {
    logoExportUrl = await fetchImageDataUrl(style.logoUrl);
  }

  // Hero photo + the us-vs-them / comparison-table supporting visuals: embed
  // each READY image as a data URL (mirrors the editor's *ExportUrl states).
  const [heroExportUrl, usVsThemExportUrl, comparisonTableExportUrl] =
    await Promise.all([
      email.heroImageStatus === "ready" && email.heroImageUrl
        ? fetchImageDataUrl(email.heroImageUrl)
        : Promise.resolve(null),
      email.usVsThemImageStatus === "ready" && email.usVsThemImageUrl
        ? fetchImageDataUrl(email.usVsThemImageUrl)
        : Promise.resolve(null),
      email.comparisonTableImageStatus === "ready" && email.comparisonTableImageUrl
        ? fetchImageDataUrl(email.comparisonTableImageUrl)
        : Promise.resolve(null),
    ]);

  // Product-list photos: index-aligned data URLs for both the "range" photos
  // and the bundle-member photos (falling back to the source URL on a failed
  // embed, matching the editor's behavior of rendering the raw URL).
  const productExportUrls = await Promise.all(
    (email.productImageUrls ?? []).map(
      async (u) => (await fetchImageDataUrl(u)) || u,
    ),
  );
  const productImageExportUrls = await Promise.all(
    (email.productListImages ?? []).map(
      async (p) => (await fetchImageDataUrl(p.imageUrl)) || p.imageUrl,
    ),
  );

  // Per-instance selected block images (image_only / image_discount,
  // Task #2286): embed each chosen/uploaded image as a same-origin data URL,
  // keyed by SOURCE url (fall back to the raw URL on a failed embed, matching
  // the editor's behavior).
  const blockImageExportUrls: Record<string, string> = {};
  await Promise.all(
    collectEmailBlockImageUrls(
      Array.isArray(email.blocks) ? (email.blocks as string[]) : [],
      email.blockCopy as EmailBlockCopy,
    ).map(async (u) => {
      blockImageExportUrls[u] = (await fetchImageDataUrl(u)) || u;
    }),
  );

  // Selling-points supporting images (W5): map instanceId -> status / ready URL,
  // and embed each ready image as a same-origin data URL so the cross-origin R2
  // image renders in (and doesn't taint) the captured PNG.
  const spImages = email.sellingPointsImages ?? [];
  const sellingPointsImageStatus: Record<string, string | null> = {};
  const sellingPointsImageUrls: Record<string, string | null> = {};
  const sellingPointsExportUrls: Record<string, string> = {};
  for (const img of spImages) {
    sellingPointsImageStatus[img.instanceId] = img.status;
    sellingPointsImageUrls[img.instanceId] = img.url;
  }
  await Promise.all(
    spImages
      .filter((img) => img.status === "ready" && img.url)
      .map(async (img) => {
        const dataUrl = await fetchImageDataUrl(img.url as string);
        if (dataUrl) sellingPointsExportUrls[img.instanceId] = dataUrl;
      }),
  );

  // Did-you-know supporting images (W8): identical capture contract.
  const dykImages = email.didYouKnowImages ?? [];
  const didYouKnowImageStatus: Record<string, string | null> = {};
  const didYouKnowImageUrls: Record<string, string | null> = {};
  const didYouKnowExportUrls: Record<string, string> = {};
  for (const img of dykImages) {
    didYouKnowImageStatus[img.instanceId] = img.status;
    didYouKnowImageUrls[img.instanceId] = img.url;
  }
  await Promise.all(
    dykImages
      .filter((img) => img.status === "ready" && img.url)
      .map(async (img) => {
        const dataUrl = await fetchImageDataUrl(img.url as string);
        if (dataUrl) didYouKnowExportUrls[img.instanceId] = dataUrl;
      }),
  );

  // Preset-template AI image slots (keyed by section id) + raw URLs used by
  // preset renderers (category-grid photos): same capture contract as the
  // archetype supporting images — every READY image becomes a same-origin data
  // URL so the cross-origin R2 image renders in (and doesn't taint) the PNG.
  const presetImgs = email.presetTemplateKey ? (email.presetImages ?? []) : [];
  const presetImageStatus: Record<string, string | null> = {};
  const presetImageUrls: Record<string, string | null> = {};
  const presetExportUrls: Record<string, string> = {};
  const presetUrlExportUrls: Record<string, string> = {};
  for (const img of presetImgs) {
    presetImageStatus[img.instanceId] = img.status;
    presetImageUrls[img.instanceId] = img.url;
  }
  if (email.presetTemplateKey) {
    await Promise.all(
      presetImgs
        .filter((img) => img.status === "ready" && img.url)
        .map(async (img) => {
          const dataUrl = await fetchImageDataUrl(img.url as string);
          if (dataUrl) presetExportUrls[img.instanceId] = dataUrl;
        }),
    );
    // Raw real-photo URLs referenced by preset copy (category grid rows).
    const rawUrls = new Set<string>();
    const instances = ((email.blockCopy as EmailBlockCopy) ?? {}).instances ?? {};
    for (const inst of Object.values(instances)) {
      const preset = (inst as any)?.preset;
      for (const c of preset?.categories ?? []) {
        if (c?.imageUrl) rawUrls.add(c.imageUrl as string);
      }
    }
    const second = (email.presetData as EmailPresetInputs | null)
      ?.secondProductImageUrl;
    if (second) rawUrls.add(second);
    await Promise.all(
      Array.from(rawUrls).map(async (url) => {
        const dataUrl = await fetchImageDataUrl(url);
        if (dataUrl) presetUrlExportUrls[url] = dataUrl;
      }),
    );
  }

  // Task #2556: image-treatment "selected" URLs for eligible blocks (selling_points,
  // did_you_know, us_vs_them, comparison_table, product_list). Embed as same-origin
  // data URLs so chosen images render in (and don't taint) the captured PNG.
  const imageTreatmentExportUrls: Record<string, string> = {};
  {
    const allBlocks = Array.isArray(email.blocks) ? (email.blocks as string[]) : [];
    const treatmentUrls = new Set<string>();
    for (const key of allBlocks) {
      const type = emailBlockType(key);
      if (isEmailImageTreatmentEligible(type)) {
        const inst = resolveEmailInstanceCopy(email.blockCopy as EmailBlockCopy, key);
        for (const u of collectEmailImageTreatmentUrls(inst, type)) {
          treatmentUrls.add(u);
        }
      }
    }
    await Promise.all(
      Array.from(treatmentUrls).map(async (u) => {
        const dataUrl = await fetchImageDataUrl(u);
        imageTreatmentExportUrls[u] = dataUrl || u;
      }),
    );
  }

  // Satori-baked hero band + did-you-know tiles — the SAME pixel-locked
  // typography the editor preview shows. Best-effort: on any failure the
  // renderer falls back to live-CSS bands (identical layout, CSS-rendered text).
  // Preset emails skip this entirely (no Satori-baked archetype bands).
  let rasterizedBlocks: Record<string, string> = {};
  if (!email.presetTemplateKey) try {
    const res = await fetch(
      `/api/brands/${brandId}/email-express/emails/${email.id}/preview-blocks`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          headline: email.headline ?? "",
          blockCopy: (email.blockCopy as EmailBlockCopy) ?? {},
          blocks: email.blocks ?? [],
          hiddenBlocks: email.hiddenBlocks ?? [],
        }),
        credentials: "include",
      },
    );
    if (res.ok) {
      const data = await res.json().catch(() => ({}) as any);
      if (data?.rasterizedBlocks && typeof data.rasterizedBlocks === "object") {
        rasterizedBlocks = data.rasterizedBlocks;
      }
    }
  } catch {
    // Non-fatal — live-CSS fallback.
  }

  return {
    brandName: style.brandName,
    headline: email.headline ?? "",
    bodyCopy: email.bodyCopy ?? "",
    ctaText: email.ctaText ?? "",
    blockCopy: (email.blockCopy as EmailBlockCopy) ?? {},
    sale: saleTextOf(email),
    style,
    footer: resolveEffectiveEmailFooter(
      (email.universalAssets?.footer?.content ?? null) as UniversalFooterContent | null,
      (email.footerOverrides ?? null) as EmailFooterOverrides | null,
      brandFooterSettings,
    ),
    logoExportUrl,
    designWidth: EMAIL_CAPTURE_WIDTH,
    heroImageUrl: email.heroImageUrl,
    heroImageStatus: email.heroImageStatus,
    heroExportUrl,
    usVsThemImageUrl: email.usVsThemImageUrl,
    usVsThemImageStatus: email.usVsThemImageStatus,
    usVsThemExportUrl,
    comparisonTableImageUrl: email.comparisonTableImageUrl,
    comparisonTableImageStatus: email.comparisonTableImageStatus,
    comparisonTableExportUrl,
    productImageUrls: email.productImageUrls ?? undefined,
    productListImages: email.productListImages ?? undefined,
    productExportUrls,
    productImageExportUrls,
    blockImageExportUrls,
    sellingPointsImageStatus,
    sellingPointsImageUrls,
    sellingPointsExportUrls,
    didYouKnowImageStatus,
    didYouKnowImageUrls,
    didYouKnowExportUrls,
    rasterizedBlocks,
    presetTemplateKey: email.presetTemplateKey ?? null,
    presetData: (email.presetData as EmailPresetInputs | null) ?? null,
    presetImageStatus,
    presetImageUrls,
    presetExportUrls,
    presetUrlExportUrls,
    imageTreatmentExportUrls,
  };
}

// Render the email's visible blocks off-screen at the editor's desktop design
// width, capture ONE retina PNG per section, upload each to durable storage,
// and return the ordered captured-block list (the canonical send input).
// Callers are responsible for having waited for pending assets first (see
// `waitForEmailAssetsSettled`).
export async function captureEmailBlocks(opts: {
  brandId: string;
  email: EmailDetail;
  brand: any;
}): Promise<EmailCapturedBlock[]> {
  const { brandId, email, brand } = opts;
  const linkConfig = (email.linkConfig as EmailLinkConfig) ?? DEFAULT_LINK_CONFIG;

  const visible = visibleBlocksOf(email);
  if (visible.length === 0) throw new Error("Nothing to capture");

  const ctx = await buildEmailCaptureCtx(brandId, email, brand);

  // Off-screen mount: positioned out of view (not display:none, which breaks
  // html-to-image) at the exact editor design width for a pixel-identical
  // capture.
  const container = document.createElement("div");
  container.style.position = "fixed";
  container.style.top = "0";
  container.style.left = "-10000px";
  container.style.width = `${EMAIL_CAPTURE_WIDTH}px`;
  container.style.pointerEvents = "none";
  container.setAttribute("aria-hidden", "true");
  document.body.appendChild(container);

  const root = createRoot(container);
  try {
    root.render(
      <div
        className="w-full bg-white text-neutral-900 overflow-hidden"
        data-testid="email-capture-document"
      >
        {visible.map((key) => (
          // Chrome-free capture wrapper: deliberately NOT PreviewBlock, so no
          // editor-only chrome (hover overlay, label chip, selection ring) can
          // ever leak into the captured/sent raster — defense in depth beyond
          // the CAPTURE_OPTS data-export-exclude filter.
          <div key={key} data-testid={`preview-block-${key}`}>
            {renderPreviewBlock(key, ctx)}
          </div>
        ))}
      </div>,
    );

    // Let React commit + paint, then wait for web fonts so text metrics settle.
    await nextFrame();
    try {
      await (document as any).fonts?.ready;
    } catch {}
    await nextFrame();

    const { toBlob } = await import("html-to-image");
    const nameSlug = sanitizeFileName(email.name);
    const captured: EmailCapturedBlock[] = [];
    for (const key of visible) {
      const node = container.querySelector<HTMLElement>(
        `[data-testid="preview-block-${key}"]`,
      );
      if (!node) continue;
      const blob = await toBlob(node, CAPTURE_OPTS);
      if (!blob) continue;
      const url = await uploadBlockImage(blob, `${nameSlug}-${key}.png`);
      const linkUrl = linkForBlock(linkConfig, key);
      const hasImagery =
        emailBlockType(key) === "body"
          ? resolveEmailBodyImagePreference(
              resolveEmailInstanceCopy(email.blockCopy, key),
            ).mode === "selected"
          : undefined;
      captured.push({
        blockKey: key,
        url,
        ...(linkUrl ? { linkUrl } : {}),
        ...(hasImagery !== undefined ? { hasImagery } : {}),
      });
    }
    if (captured.length === 0) throw new Error("Nothing to capture");
    return captured;
  } finally {
    root.unmount();
    container.remove();
  }
}

// Capture an email's sections and PERSIST them on the email row (without
// sending). Used by flow sends, which prepare every member email's captures
// up-front so the server-side flow worker can build each message without a
// browser. Waits for pending assets first. Returns the captured list.
export async function captureAndPersistEmailBlocks(opts: {
  brandId: string;
  email: EmailDetail;
  brand: any;
}): Promise<EmailCapturedBlock[]> {
  const { brandId, brand } = opts;
  const email = await waitForEmailAssetsSettled(brandId, opts.email.id);
  const blocks = await captureEmailBlocks({ brandId, email, brand });
  // Stamp the content hash on the first entry so the editor can tell after a
  // reload whether the captured preview is behind the email's content.
  if (blocks.length > 0) {
    blocks[0].sourceHash = computeEmailPolishSourceHash(email as any);
  }
  const { apiRequest } = await import("@/lib/queryClient");
  await apiRequest(
    "PUT",
    `/api/brands/${brandId}/email-express/emails/${email.id}/captured-blocks`,
    { blocks },
  );
  return blocks;
}

// Render the email's visible blocks off-screen, capture each to a PNG, upload to
// R2, then POST to the send endpoint (which persists the captures on the email
// row BEFORE claiming the async send). `brand` is the full brand row (for the
// brand-style resolver). Throws on a missing link config or any
// capture/upload/send failure, matching the editor.
export async function captureAndSendEmail(opts: {
  brandId: string;
  email: EmailDetail;
  brand: any;
  force?: boolean;
}): Promise<KlaviyoSendResult> {
  const { brandId, brand, force } = opts;

  // Require at least one usable link so the draft's CTAs aren't dead (parity
  // with the editor's send guard).
  if (!emailLinkConfigHasUsableLink(opts.email.linkConfig as EmailLinkConfig | null)) {
    throw new Error(
      "This email doesn't have a destination link yet, so its buttons wouldn't go anywhere. Open it in the editor and add a link in the Links section of the left sidebar, then try sending again.",
    );
  }

  // A 202/pending slot must never be captured — wait for in-flight copy/images
  // to settle and capture from the freshest detail.
  const email = await waitForEmailAssetsSettled(brandId, opts.email.id);
  const blocks = await captureEmailBlocks({ brandId, email, brand });
  if (blocks.length > 0) {
    blocks[0].sourceHash = computeEmailPolishSourceHash(email as any);
  }

  const { apiRequest } = await import("@/lib/queryClient");
  const res = await apiRequest(
    "POST",
    `/api/brands/${brandId}/email-express/emails/${email.id}/send`,
    force ? { blocks, force: true } : { blocks },
  );
  return (await res.json()) as KlaviyoSendResult;
}

// Pre-capture every member email of a flow BEFORE the flow send is claimed, so
// the server-side flow worker can build each email from its captured preview
// sections. Polished emails are skipped (the worker sends their canonical
// polished image stack); anything else gets a fresh off-screen capture
// persisted via PUT /captured-blocks. Sequential on purpose: each capture
// mounts an off-screen DOM + rasterizes several blocks, and parallel captures
// would thrash the browser.
export async function precaptureFlowEmails(opts: {
  brandId: string;
  emailIds: string[];
}): Promise<void> {
  const { brandId, emailIds } = opts;
  if (emailIds.length === 0) return;
  const { apiRequest } = await import("@/lib/queryClient");
  const brandRes = await apiRequest("GET", `/api/brands/${brandId}`);
  const brand = await brandRes.json();
  for (const id of emailIds) {
    const res = await apiRequest(
      "GET",
      `/api/brands/${brandId}/email-express/emails/${id}`,
    );
    const detail = (await res.json()) as EmailDetail;
    // Polished → the worker compiles the polished blocks; no capture needed.
    if ((detail as any).polishStatus === "ready") continue;
    await captureAndPersistEmailBlocks({ brandId, email: detail, brand });
  }
}
